import React, { useEffect, useState, useRef, Component } from 'react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, HomeIcon, LockIcon, UnlockIcon, ChevronRightIcon, ChevronDownIcon, CheckIcon, ArrowRightIcon, ShareIcon, DownloadIcon, RefreshCwIcon, CreditCardIcon, ReceiptIcon, SettingsIcon, ShieldIcon, SlidersIcon, EyeIcon, EyeOffIcon, PlusIcon, Trash2Icon, AlertCircleIcon, SmartphoneIcon, GlobeIcon, WifiIcon, MapPinIcon, XIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
interface CardsScreenProps {
  onBack?: () => void;
  onNavigateHome?: () => void;
}
type Step = 'tabs' | 'summary' | 'pay_bill' | 'review' | 'processing' | 'success' | 'manage_cards' | 'card_detail' | 'card_controls' | 'spending_limits' | 'blocked_merchants' | 'pin_management' | 'pin_enter_old' | 'pin_enter_new' | 'pin_confirm_new' | 'pin_success' | 'activation_entry' | 'activation_details' | 'activation_set_pin' | 'activation_success' | 'request_entry' | 'request_type' | 'request_delivery' | 'request_review' | 'request_processing' | 'request_success' | 'apply_account' | 'apply_scheme' | 'apply_choose_card' | 'apply_name_delivery' | 'apply_review' | 'apply_processing' | 'apply_success';
type TabId = 'credit' | 'debit';
const CAROUSEL_CARDS = [{
  id: 'cc-1',
  type: 'credit',
  label: 'Gold',
  number: '•••• •••• •••• 7231',
  expires: '06/27',
  isNew: false,
  isActive: true
}, {
  id: 'cc-2',
  type: 'credit',
  label: 'Platinum',
  number: '•••• •••• •••• 8892',
  expires: '09/28',
  isNew: true,
  isActive: false,
  inactiveReason: 'Pending Activation'
}, {
  id: 'db-1',
  type: 'debit',
  label: 'Classic',
  number: '•••• •••• •••• 4892',
  expires: '12/28',
  isNew: false,
  isActive: true
}, {
  id: 'db-2',
  type: 'debit',
  label: 'Platinum',
  number: '•••• •••• •••• 5567',
  expires: '03/29',
  isNew: false,
  isActive: false,
  inactiveReason: 'Locked by User'
}];
const CREDIT_CARD = {
  id: 'cc-1',
  type: 'CREDIT' as const,
  tier: 'Gold',
  number: '•••• •••• •••• 7231',
  fullNumber: '4521 1234 5678 7231',
  cardholder: 'KASHISH',
  expires: '06/27',
  totalLimit: 250000,
  availableLimit: 170000,
  minDue: 800,
  totalDue: 80000,
  dueDate: '15 Jun 2026',
  status: 'Active'
};
const DEBIT_CARDS = [{
  id: 'db-1',
  type: 'DEBIT' as const,
  tier: 'Classic',
  number: '•••• •••• •••• 4892',
  cardholder: 'KASHISH',
  expires: '12/28',
  status: 'Active'
}, {
  id: 'db-2',
  type: 'DEBIT' as const,
  tier: 'Platinum',
  number: '•••• •••• •••• 5567',
  cardholder: 'KASHISH',
  expires: '03/29',
  status: 'Locked'
}];
const VIRTUAL_CARDS = [{
  id: 'vc-1',
  type: 'VIRTUAL' as const,
  tier: 'E-Card',
  number: '•••• •••• •••• 9912',
  fullNumber: '4242 4242 4242 9912',
  cvv: '123',
  cardholder: 'KASHISH',
  expires: '01/25',
  status: 'Active'
}];
const PAY_FROM_ACCOUNTS = [{
  id: 'current',
  holder: 'Kashish',
  label: 'Current Account',
  number: '......0123 4567 8910',
  balance: 282992
}, {
  id: 'savings',
  holder: 'Kashish',
  label: 'Savings Account',
  number: '......0456 7890 1234',
  balance: 145200
}];
const TRANSACTIONS = [{
  id: 1,
  merchant: 'Netflix',
  date: 'Today, 10:23 AM',
  amount: 1500,
  type: 'debit'
}, {
  id: 2,
  merchant: 'Foodpanda',
  date: 'Yesterday, 8:45 PM',
  amount: 2350,
  type: 'debit'
}, {
  id: 3,
  merchant: 'Uber',
  date: '12 Jun, 9:15 AM',
  amount: 850,
  type: 'debit'
}, {
  id: 4,
  merchant: 'Payment Received',
  date: '10 Jun, 2:00 PM',
  amount: 50000,
  type: 'credit'
}];
const BLOCKED_MERCHANTS = [{
  id: 1,
  name: 'Daraz.pk',
  date: '15 May 2026'
}, {
  id: 2,
  name: 'AliExpress',
  date: '02 Jun 2026'
}];
const slideVariants = {
  enter: (dir: number) => ({
    x: dir > 0 ? '100%' : '-100%',
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: 'easeOut'
    }
  },
  exit: (dir: number) => ({
    x: dir < 0 ? '100%' : '-100%',
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: 'easeIn'
    }
  })
};
// ===== Visual Components =====
function CreditCardVisual({
  number,
  cardholder,
  expires,
  small = false,
  showFull = false,
  fullNumber = ''
}: any) {
  return <div className={`w-full ${small ? 'aspect-[1.586/1]' : 'aspect-[1.586/1]'} rounded-2xl relative overflow-hidden`} style={{
    background: 'linear-gradient(135deg, #1A0E08 0%, #3D1A0A 45%, #C0542A 100%)',
    boxShadow: '0 12px 32px rgba(58,18,8,0.35)'
  }} data-id="element-316" data-mp="jcm1e25cpby4">
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(circle at 110% -10%, rgba(243,112,36,0.45) 0%, rgba(243,112,36,0) 55%)'
    }} data-id="element-317" data-mp="1g1bnk4o0gqpw" />
      <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.08]" style={{
      backgroundImage: 'repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 18px)'
    }} data-id="element-318" data-mp="170n6f13bma8s" />
      <div className="relative z-10 p-5 h-full flex flex-col justify-between" data-id="element-319" data-mp="3rquq2z4r5il0">
        <div className="flex justify-between items-start" data-id="element-320" data-mp="3rorqbndssb33">
          <div data-id="element-321" data-mp="3j8sn3dmtuwa8">
            <p className="text-white/70 text-[9px] font-bold uppercase tracking-[0.18em] mb-1" data-id="element-322" data-mp="3j8als2lb5rvg">
              CREDIT
            </p>
            <p className="text-white text-[16px] font-bold" data-id="element-323" data-mp="3t89wo6579ubh">Gold</p>
          </div>
          <img src="https://cdn.magicpatterns.com/uploads/2a6tXHiqGQDbd8PcjyC69q/Dark_theme_logo-removebg-preview.png" alt="" className="h-7 w-auto brightness-0 invert" data-id="element-324" data-mp="3t5xf3u3ctlyy" />
        </div>
        <div className="flex items-center gap-2 -mt-1" data-id="element-325" data-mp="30jvtzd3629tl">
          <div className="w-9 h-7 rounded-md" style={{
          background: 'linear-gradient(135deg, #F9D07A 0%, #E0A640 50%, #F9D07A 100%)',
          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.18)'
        }} data-id="element-326" data-mp="2vlau5ertts5o" />
        </div>
        <div data-id="element-327" data-mp="35h4evlfp8ryj">
          <p className="text-white text-[18px] font-mono tracking-[0.18em] mb-3" data-id="element-328" data-mp="35he6fdge776c">
            {showFull ? fullNumber : number}
          </p>
          <div className="flex justify-between items-end" data-id="element-329" data-mp="2cubcyzqkpmmy">
            <div data-id="element-330" data-mp="2crxh4oo2k6ec">
              <p className="text-white/60 text-[8px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-331" data-mp="gqwsfnn4gj30">
                Cardholder
              </p>
              <p className="text-white text-[13px] font-bold uppercase tracking-wider" data-id="element-332" data-mp="loyd2d074qgl">
                {cardholder}
              </p>
            </div>
            <div className="text-right" data-id="element-333" data-mp="qj2ll0p416js">
              <p className="text-white/60 text-[8px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-334" data-mp="uh8lborj6lv7">
                Valid Thru
              </p>
              <p className="text-white text-[13px] font-bold" data-id="element-335" data-mp="3tzhx2kosjiww">{expires}</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function DebitCardVisual({
  number,
  cardholder,
  expires,
  tier,
  showFull = false,
  fullNumber = ''
}: any) {
  const gradient = tier === 'Platinum' ? 'linear-gradient(135deg, #1A1A2E 0%, #16213E 100%)' : 'linear-gradient(135deg, #661806 0%, #3D1A0A 100%)';
  return <div className="w-full aspect-[1.586/1] rounded-2xl relative overflow-hidden" style={{
    background: gradient,
    boxShadow: '0 10px 24px rgba(0,0,0,0.22)'
  }} data-id="element-336" data-mp="1n6z2y7sdg5w2">
      <div className="relative z-10 p-5 h-full flex flex-col justify-between" data-id="element-337" data-mp="1xa4catl0u152">
        <div className="flex justify-between items-start" data-id="element-338" data-mp="1sd45gggckucw">
          <div data-id="element-339" data-mp="1nkumjkdnguk4">
            <p className="text-white/70 text-[9px] font-bold uppercase tracking-[0.18em] mb-1" data-id="element-340" data-mp="1nkb13tosjo1c">
              DEBIT
            </p>
            <p className="text-white text-[16px] font-bold" data-id="element-341" data-mp="1jizk2cjzgb4r">{tier}</p>
          </div>
          <img src="https://cdn.magicpatterns.com/uploads/2a6tXHiqGQDbd8PcjyC69q/Dark_theme_logo-removebg-preview.png" alt="" className="h-7 w-auto brightness-0 invert" data-id="element-342" data-mp="1el7jyv96piph" />
        </div>
        <div data-id="element-343" data-mp="14lgq8vsg77qg">
          <p className="text-white text-[18px] font-mono tracking-[0.18em] mb-3" data-id="element-344" data-mp="14n05qk6a5er6">
            {showFull ? fullNumber : number}
          </p>
          <div className="flex justify-between items-end" data-id="element-345" data-mp="10mzrp0n3f5kn">
            <div data-id="element-346" data-mp="2yyygwro5gkpp">
              <p className="text-white/60 text-[8px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-347" data-mp="2yzghg0huhl3f">
                Cardholder
              </p>
              <p className="text-white text-[13px] font-bold uppercase tracking-wider" data-id="element-348" data-mp="3e6w87u4h3cs9">
                {cardholder}
              </p>
            </div>
            <div className="text-right" data-id="element-349" data-mp="3i87xtzncjy0g">
              <p className="text-white/60 text-[8px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-350" data-mp="34huvcct85bii">
                Valid Thru
              </p>
              <p className="text-white text-[13px] font-bold" data-id="element-351" data-mp="39f4rscf16nfu">{expires}</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function VirtualCardVisual({
  number,
  cardholder,
  expires,
  showFull = false,
  fullNumber = '',
  cvv = ''
}: any) {
  return <div className="w-full aspect-[1.586/1] rounded-2xl relative overflow-hidden" style={{
    background: 'linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.22)'
  }} data-id="element-352" data-mp="2ljif673wb1df">
      <div className="relative z-10 p-5 h-full flex flex-col justify-between" data-id="element-353" data-mp="jrx03xx6w29v">
        <div className="flex justify-between items-start" data-id="element-354" data-mp="opfdq1m5j5o7">
          <div data-id="element-355" data-mp="yuf07fjtzoqq">
            <p className="text-white/70 text-[9px] font-bold uppercase tracking-[0.18em] mb-1" data-id="element-356" data-mp="ytvsssrt4iqy">
              VIRTUAL
            </p>
            <p className="text-white text-[16px] font-bold" data-id="element-357" data-mp="twvl6dfb77xq">E-Card</p>
          </div>
          <WifiIcon className="w-6 h-6 text-white/50" data-id="element-358" data-mp="17nqvlu5ghx3f" />
        </div>
        <div data-id="element-359" data-mp="12w8vt9f662k1">
          <p className="text-white text-[18px] font-mono tracking-[0.18em] mb-3" data-id="element-360" data-mp="12vqqlnaj4dzz">
            {showFull ? fullNumber : number}
          </p>
          <div className="flex justify-between items-end" data-id="element-361" data-mp="1hj23bsig641o">
            <div data-id="element-362" data-mp="1cmlcgqjv5bkw">
              <p className="text-white/60 text-[8px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-363" data-mp="1cl0kn8oxo6qq">
                Cardholder
              </p>
              <p className="text-white text-[13px] font-bold uppercase tracking-wider" data-id="element-364" data-mp="1mjzv8wt9ecgd">
                {cardholder}
              </p>
            </div>
            <div className="flex gap-4 text-right" data-id="element-365" data-mp="3sdps97h1wjqq">
              {showFull && <div data-id="element-366" data-mp="3oburc43188dh">
                  <p className="text-white/60 text-[8px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-367" data-mp="3oce8vjorsuqz">
                    CVV
                  </p>
                  <p className="text-white text-[13px] font-bold" data-id="element-368" data-mp="2owvaok2660zm">{cvv}</p>
                </div>}
              <div data-id="element-369" data-mp="2kzz7uhuv7nt9">
                <p className="text-white/60 text-[8px] font-bold uppercase tracking-[0.18em] mb-0.5" data-id="element-370" data-mp="2fy9s4owp6lyu">
                  Valid Thru
                </p>
                <p className="text-white text-[13px] font-bold" data-id="element-371" data-mp="2b0zwgripte2k">{expires}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
export function CardsScreen({
  onBack,
  onNavigateHome
}: CardsScreenProps) {
  const {
    isDark
  } = useTheme();
  const [step, setStep] = useState<Step>('tabs');
  const [direction, setDirection] = useState(1);
  const [tab, setTab] = useState<TabId>('credit');
  const [isLocked, setIsLocked] = useState(false);
  const [selectedCard, setSelectedCard] = useState<any>(CREDIT_CARD);
  // Toggles state
  const [toggles, setToggles] = useState({
    ecommerce: true,
    contactless: true,
    international: false,
    atm: true
  });
  const [pendingToggle, setPendingToggle] = useState<string | null>(null);
  // Block card flow state
  const [blockReason, setBlockReason] = useState<string | null>(null);
  // Virtual card state
  const [showVirtualDetails, setShowVirtualDetails] = useState(false);
  // PIN state
  const [pinInput, setPinInput] = useState('');
  // Pay-bill state
  const [payAmount, setPayAmount] = useState<string>('');
  const [payFrom, setPayFrom] = useState(PAY_FROM_ACCOUNTS[0]);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  // Apply Flow State
  const [selectedApplyAccount, setSelectedApplyAccount] = useState<string | null>(null);
  const [selectedScheme, setSelectedScheme] = useState<'paypak' | 'mastercard' | null>(null);
  const [selectedApplyCard, setSelectedApplyCard] = useState<any>(null);
  const [expandedApplyCard, setExpandedApplyCard] = useState<string | null>(null);
  const [nameOnCard, setNameOnCard] = useState('');
  const [applyAddressType, setApplyAddressType] = useState<'home' | 'office'>('home');
  const [applyAddress, setApplyAddress] = useState('');
  const [applyTermsAgreed, setApplyTermsAgreed] = useState(false);
  // Carousel State
  const [cardStatuses, setCardStatuses] = useState<Record<string, boolean>>({
    'cc-1': true,
    'cc-2': false,
    'db-1': true,
    'db-2': false
  });
  const [carouselActiveIndex, setCarouselActiveIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const navigateTo = (next: Step, dir = 1) => {
    setDirection(dir);
    setStep(next);
  };
  const formatPKR = (n: number) => n.toLocaleString('en-US', {
    minimumFractionDigits: 0
  });
  useEffect(() => {
    if (step === 'processing' || step === 'request_processing' || step === 'apply_processing') {
      const t = setTimeout(() => {
        if (step === 'processing') navigateTo('success');else if (step === 'request_processing') navigateTo('request_success');else navigateTo('apply_success');
      }, 1800);
      return () => clearTimeout(t);
    }
  }, [step]);
  // Carousel scroll listener
  useEffect(() => {
    const root = carouselRef.current;
    if (!root || step !== 'tabs') return;
    const updateActive = () => {
      const rootRect = root.getBoundingClientRect();
      const rootCenter = rootRect.left + rootRect.width / 2;
      let closestIdx = 0;
      let closestDist = Infinity;
      cardRefs.current.forEach((el, i) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const center = rect.left + rect.width / 2;
        const dist = Math.abs(center - rootCenter);
        if (dist < closestDist) {
          closestDist = dist;
          closestIdx = i;
        }
      });
      setCarouselActiveIndex(closestIdx);
    };
    root.addEventListener('scroll', updateActive, {
      passive: true
    });
    // Reset scroll when tab changes
    root.scrollTo({
      left: 0,
      behavior: 'instant'
    });
    updateActive();
    return () => root.removeEventListener('scroll', updateActive);
  }, [tab, step]);
  const textPri = isDark ? 'text-white' : 'text-[#303030]';
  const textSec = isDark ? 'text-[#B8A89E]' : 'text-[#818285]';
  const cardSurface = isDark ? 'bg-[#2D1810] border border-[rgba(243,112,36,0.12)]' : 'bg-white border border-[#F0E8E3]';
  // ===== Reusable Components =====
  const Header = ({
    title,
    onBackClick = () => navigateTo('tabs', -1)
  }: {
    title: string;
    onBackClick?: () => void;
  }) => <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-372" data-mp="3usc1bi2jn2j9">
      <button onClick={onBackClick} aria-label="Back" className={`p-2 -ml-2 ${textPri}`} data-id="element-373" data-mp="3uslu1dez3nsn">
        <ArrowLeftIcon className="w-6 h-6" strokeWidth={2} data-id="element-374" data-mp="3pv2d25qw4wx0" />
      </button>
      <h1 className={`text-[18px] font-bold ${textPri}`} data-id="element-375" data-mp="3lqm8q8wxdxjw">{title}</h1>
      <button onClick={onNavigateHome} aria-label="Home" className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${isDark ? 'border-[#F37024] text-[#F37024]' : 'border-[#5A1408] text-[#5A1408]'}`} data-id="element-376" data-mp="3gst5noqx5jnu">
        <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-377" data-mp="218ts1uoorhyk" />
      </button>
    </div>;
  const PinEntry = ({
    title,
    subtitle,
    onComplete
  }: {
    title: string;
    subtitle: string;
    onComplete: () => void;
  }) => <div className="px-6 flex-1 flex flex-col items-center justify-center" data-id="element-378" data-mp="2a9ronrwson74">
      <h2 className={`text-[24px] font-bold mb-2 ${textPri}`} data-id="element-379" data-mp="1dktw8gcip1d4">{title}</h2>
      <p className={`text-[14px] mb-8 text-center ${textSec}`} data-id="element-380" data-mp="1hlcmw72oc1dq">{subtitle}</p>
      <div className="flex gap-4 mb-12" data-id="element-381" data-mp="1hj9niwfmsvwc">
        {[0, 1, 2, 3].map(i => <div key={i} className={`w-12 h-14 rounded-xl border-2 flex items-center justify-center text-[24px] font-bold ${pinInput.length > i ? isDark ? 'border-[#F37024] text-white' : 'border-[#5A1408] text-[#303030]' : isDark ? 'border-[#3D2518]' : 'border-[#E5D5C5]'}`} data-id="element-382" data-mp="1mjxghnnf7mt7">
            {pinInput.length > i ? '•' : ''}
          </div>)}
      </div>
      <div className="grid grid-cols-3 gap-x-8 gap-y-6 w-full max-w-[280px]" data-id="element-383" data-mp="22620i3nk450o">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => <button key={num} onClick={() => {
        if (pinInput.length < 4) {
          const newPin = pinInput + num;
          setPinInput(newPin);
          if (newPin.length === 4) setTimeout(onComplete, 300);
        }
      }} className={`text-[24px] font-medium py-2 active:scale-90 transition-transform ${textPri}`} data-id="element-384" data-mp="1x7h30bzovzfx">
            {num}
          </button>)}
        <div data-id="element-385" data-mp="1j90viiu064wq" />
        <button onClick={() => {
        if (pinInput.length < 4) {
          const newPin = pinInput + '0';
          setPinInput(newPin);
          if (newPin.length === 4) setTimeout(onComplete, 300);
        }
      }} className={`text-[24px] font-medium py-2 active:scale-90 transition-transform ${textPri}`} data-id="element-386" data-mp="1e834rhuxg2jm">
          0
        </button>
        <button onClick={() => setPinInput(prev => prev.slice(0, -1))} className={`flex items-center justify-center py-2 active:scale-90 transition-transform ${textPri}`} data-id="element-387" data-mp="33lq2z5r7ghvp">
          <ArrowLeftIcon className="w-6 h-6" data-id="element-388" data-mp="374fetub87ay3" />
        </button>
      </div>
    </div>;
  const SuccessView = ({
    title,
    subtitle,
    actionText,
    onAction
  }: {
    title: string;
    subtitle: string;
    actionText: string;
    onAction: () => void;
  }) => <div className="px-6 flex-1 flex flex-col items-center justify-center text-center" data-id="element-389" data-mp="2x93zb9rztzpa">
      <motion.div initial={{
      scale: 0
    }} animate={{
      scale: 1
    }} transition={{
      type: 'spring',
      damping: 14,
      stiffness: 200
    }} className="w-20 h-20 rounded-full bg-[#10B981]/15 flex items-center justify-center mb-6" data-id="element-390" data-mp="327yfs52hnc68">
        <div className="w-14 h-14 rounded-full bg-[#10B981] flex items-center justify-center" data-id="element-391" data-mp="3g4lpl8fjhwtk">
          <CheckIcon className="w-8 h-8 text-white" strokeWidth={3} data-id="element-392" data-mp="3l3yavoqrivu1" />
        </div>
      </motion.div>
      <h2 className={`text-[24px] font-bold mb-2 ${textPri}`} data-id="element-393" data-mp="con2b3sghn0q">{title}</h2>
      <p className={`text-[14px] mb-8 ${textSec}`} data-id="element-394" data-mp="hfd5qbhbnfss">{subtitle}</p>
      <button onClick={onAction} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-395" data-mp="hhpvvbx8gwgx">
        {actionText}
      </button>
    </div>;
  // ===== Apply flow steps render functions =====
  const renderApplyAccount = () => <motion.div key="apply_account" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-396" data-mp="23t81k1adpv9y">
      <Header title="Select Account" data-id="element-397" data-mp="2rij7ryai0hvh" />
      <div className="px-6 mt-4 flex-1" data-id="element-398" data-mp="2rjlxi2w34e9t">
        <p className={`text-[14px] mb-6 ${textSec}`} data-id="element-399" data-mp="2rgg5ofw2myae">
          Select the account you want to link to your new card.
        </p>
        <div className="space-y-3" data-id="element-400" data-mp="2ii4i6mqi4gm6">
          {PAY_FROM_ACCOUNTS.map(acc => <button key={acc.id} onClick={() => setSelectedApplyAccount(acc.id)} className={`w-full p-4 rounded-2xl border-2 text-left transition-colors ${selectedApplyAccount === acc.id ? isDark ? 'border-[#F37024] bg-[#F37024]/10' : 'border-[#F37024] bg-[#F37024]/5' : cardSurface}`} data-id="element-401" data-mp="2ijyvbevj74fp">
              <div className="flex items-center justify-between mb-1" data-id="element-402" data-mp="3irvvfj7k2kpp">
                <span className={`text-[14px] font-bold ${textPri}`} data-id="element-403" data-mp="3nq72jm6guh48">
                  {acc.label}
                </span>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedApplyAccount === acc.id ? 'border-[#F37024]' : isDark ? 'border-[#3D2518]' : 'border-[#E5D5C5]'}`} data-id="element-404" data-mp="3rp636ul5zrpv">
                  {selectedApplyAccount === acc.id && <div className="w-2.5 h-2.5 rounded-full bg-[#F37024]" data-id="element-405" data-mp="a9zd7n2plro3" />}
                </div>
              </div>
              <p className={`text-[12px] ${textSec} mb-2`} data-id="element-406" data-mp="f92k7d2927yp">{acc.number}</p>
              <p className={`text-[14px] font-bold ${textPri}`} data-id="element-407" data-mp="jaxhm6h28177">
                Rs. {formatPKR(acc.balance)}
              </p>
            </button>)}
        </div>
      </div>
      <div className="px-6 mt-auto pt-6" data-id="element-408" data-mp="2h2idyitmw2xw">
        <button onClick={() => navigateTo('apply_scheme')} disabled={!selectedApplyAccount} className="w-full py-4 bg-[#F37024] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-409" data-mp="2l1r31adqbcn4">
          Continue
        </button>
      </div>
    </motion.div>;
  const renderApplyScheme = () => <motion.div key="apply_scheme" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-410" data-mp="2erx4libp7uph">
      <Header title="Select Scheme" onBackClick={() => navigateTo('apply_account', -1)} data-id="element-411" data-mp="2skv51j300czw" />
      <div className="px-6 mt-4 flex-1" data-id="element-412" data-mp="2xk80gs7xqse3">
        <p className={`text-[14px] mb-6 ${textSec}`} data-id="element-413" data-mp="2xlj5swh1487b">
          Choose the payment network for your new card.
        </p>
        <div className="space-y-3" data-id="element-414" data-mp="32ijbv7dvpayf">
          {[{
          id: 'mastercard',
          label: 'Mastercard',
          desc: 'Global acceptance, premium benefits'
        }, {
          id: 'paypak',
          label: 'PayPak',
          desc: 'Domestic acceptance, lower fees'
        }].map(scheme => <button key={scheme.id} onClick={() => setSelectedScheme(scheme.id as any)} className={`w-full p-4 rounded-2xl border-2 text-left transition-colors ${selectedScheme === scheme.id ? isDark ? 'border-[#F37024] bg-[#F37024]/10' : 'border-[#F37024] bg-[#F37024]/5' : cardSurface}`} data-id="element-415" data-mp="10nbrb6ni66au">
              <div className="flex items-center justify-between mb-1" data-id="element-416" data-mp="lyfn5kokhdfi">
                <span className={`text-[16px] font-bold ${textPri}`} data-id="element-417" data-mp="1jkwi4j9tvey3">
                  {scheme.label}
                </span>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedScheme === scheme.id ? 'border-[#F37024]' : isDark ? 'border-[#3D2518]' : 'border-[#E5D5C5]'}`} data-id="element-418" data-mp="1em33gnit1hwc">
                  {selectedScheme === scheme.id && <div className="w-2.5 h-2.5 rounded-full bg-[#F37024]" data-id="element-419" data-mp="14pqeg2y7fs7x" />}
                </div>
              </div>
              <p className={`text-[13px] ${textSec}`} data-id="element-420" data-mp="3pdus47i38wts">{scheme.desc}</p>
            </button>)}
        </div>
      </div>
      <div className="px-6 mt-auto pt-6" data-id="element-421" data-mp="2hdw53oumf2se">
        <button onClick={() => navigateTo('apply_choose_card')} disabled={!selectedScheme} className="w-full py-4 bg-[#F37024] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-422" data-mp="2hexfdr3tbl92">
          Continue
        </button>
      </div>
    </motion.div>;
  const renderApplyChooseCard = () => {
    const cards = [{
      id: 'gold',
      name: 'BOP Gold Mastercard',
      tier: 'Gold',
      fee: 'Rs. 2,500',
      apr: '38%',
      rewards: '1x Points',
      lounge: 'No'
    }, {
      id: 'platinum',
      name: 'BOP Platinum Mastercard',
      tier: 'Platinum',
      fee: 'Rs. 5,000',
      apr: '36%',
      rewards: '2x Points',
      lounge: 'Domestic Only'
    }, {
      id: 'world',
      name: 'BOP World Mastercard',
      tier: 'World',
      fee: 'Rs. 12,000',
      apr: '34%',
      rewards: '3x Points',
      lounge: 'Global Access'
    }];
    return <motion.div key="apply_choose_card" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-423" data-mp="103txrlcs3rlv">
        <Header title="Choose Your Card" onBackClick={() => navigateTo('apply_scheme', -1)} data-id="element-424" data-mp="1a4cql5mbmifx" />
        <div className="px-6 mt-4 flex-1 overflow-y-auto" data-id="element-425" data-mp="152x5xeo22dy2">
          <p className={`text-[14px] mb-6 ${textSec}`} data-id="element-426" data-mp="1j37sw6m8v8dy">
            Select the card tier that best fits your lifestyle.
          </p>
          <div className="space-y-4" data-id="element-427" data-mp="1f133jk2twxx4">
            {cards.map(card => {
            const isSelected = selectedApplyCard?.id === card.id;
            const isExpanded = expandedApplyCard === card.id;
            return <div key={card.id} className={`rounded-2xl border-2 overflow-hidden transition-colors ${isSelected ? isDark ? 'border-[#F37024] bg-[#F37024]/5' : 'border-[#F37024] bg-[#F37024]/5' : cardSurface}`} data-id="element-428" data-mp="1nupxr5re1t47">
                  <button onClick={() => {
                setSelectedApplyCard(card);
                setExpandedApplyCard(isExpanded ? null : card.id);
              }} className="w-full p-4 text-left flex items-center justify-between" data-id="element-429" data-mp="1xu70bebcfqvy">
                    <div data-id="element-430" data-mp="4vrx48yli98j">
                      <p className={`text-[15px] font-bold ${textPri}`} data-id="element-431" data-mp="4wjlaypv54os">
                        {card.name}
                      </p>
                      <p className={`text-[13px] ${textSec}`} data-id="element-432" data-mp="9wo26puoo1fj">
                        Annual Fee: {card.fee}
                      </p>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-433" data-mp="3dcczv80mw637">
                      <ChevronDownIcon className={`w-5 h-5 ${textSec} transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-434" data-mp="3iaemu416kpjy" />
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-[#F37024]' : isDark ? 'border-[#3D2518]' : 'border-[#E5D5C5]'}`} data-id="element-435" data-mp="3n9rer36wsmte">
                        {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-[#F37024]" data-id="element-436" data-mp="12i7k1q9sjz3h" />}
                      </div>
                    </div>
                  </button>
                  <AnimatePresence data-id="element-437" data-mp="2i2826rmvrubn">
                    {isExpanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden" data-id="element-438" data-mp="2i2q9cjb2zsyc">
                        <div className="px-4 pb-4 pt-2 border-t border-black/5 dark:border-white/5" data-id="element-439" data-mp="23az7i444firn">
                          <div className="grid grid-cols-2 gap-y-3 gap-x-4" data-id="element-440" data-mp="1ze34a0swn3kr">
                            <div data-id="element-441" data-mp="1ucvyu8e8igh9">
                              <p className={`text-[11px] ${textSec}`} data-id="element-442" data-mp="1pdj30y5dxz2j">APR</p>
                              <p className={`text-[13px] font-bold ${textPri}`} data-id="element-443" data-mp="1pfnga6p6cafh">
                                {card.apr}
                              </p>
                            </div>
                            <div data-id="element-444" data-mp="3jsdakpjgflne">
                              <p className={`text-[11px] ${textSec}`} data-id="element-445" data-mp="1i2o3p01z0rhk">
                                Rewards
                              </p>
                              <p className={`text-[13px] font-bold ${textPri}`} data-id="element-446" data-mp="1munzcgb0cy8c">
                                {card.rewards}
                              </p>
                            </div>
                            <div className="col-span-2" data-id="element-447" data-mp="1d435f66a4hvr">
                              <p className={`text-[11px] ${textSec}`} data-id="element-448" data-mp="20t5li9l9m2o5">
                                Lounge Access
                              </p>
                              <p className={`text-[13px] font-bold ${textPri}`} data-id="element-449" data-mp="25rgw4mjdwh7f">
                                {card.lounge}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>;
          })}
          </div>
        </div>
        <div className="px-6 mt-auto pt-6" data-id="element-450" data-mp="kffdwsuxsw9r">
          <button onClick={() => navigateTo('apply_name_delivery')} disabled={!selectedApplyCard} className="w-full py-4 bg-[#F37024] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-451" data-mp="kc9qdi1yi2g5">
            Continue
          </button>
        </div>
      </motion.div>;
  };
  const renderApplyNameDelivery = () => <motion.div key="apply_name_delivery" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-452" data-mp="33jdtromi6anx">
      <Header title="Name & Delivery" onBackClick={() => navigateTo('apply_choose_card', -1)} data-id="element-453" data-mp="2oor268dumi3v" />
      <div className="px-6 mt-4 flex-1 overflow-y-auto" data-id="element-454" data-mp="2jxr4v20thy0u">
        <div className="mb-6 pointer-events-none" data-id="element-455" data-mp="2jw7preqwdt0n">
          <CreditCardVisual number="•••• •••• •••• ••••" cardholder={nameOnCard || 'YOUR NAME'} expires="MM/YY" data-id="element-456" data-mp="1w7ervcqptb3e" />
        </div>
        <div className="mb-8" data-id="element-457" data-mp="1s5jps60ymtok">
          <label className={`block text-[13px] font-bold mb-2 ${textPri}`} data-id="element-458" data-mp="1s4idjy87k159">
            Name on Card
          </label>
          <div className="relative" data-id="element-459" data-mp="2lzxatw0iear6">
            <input type="text" value={nameOnCard} onChange={e => setNameOnCard(e.target.value.toUpperCase().slice(0, 20))} placeholder="e.g. KASHISH" className={`w-full p-4 rounded-xl border-2 outline-none transition-colors ${isDark ? 'bg-[#2D1810] border-[#3D2518] text-white focus:border-[#F37024]' : 'bg-white border-[#E5D5C5] text-[#303030] focus:border-[#F37024]'}`} data-id="element-460" data-mp="2m18gy2hffulg" />
            <span className={`absolute right-4 top-4 text-[12px] ${textSec}`} data-id="element-461" data-mp="34yjb58o7bxu1">
              {nameOnCard.length}/20
            </span>
          </div>
        </div>
        <div data-id="element-462" data-mp="39rvxfc5ersjm">
          <label className={`block text-[13px] font-bold mb-3 ${textPri}`} data-id="element-463" data-mp="3drd5mdc7z3sh">
            Delivery Address
          </label>
          <div className="flex gap-3 mb-4" data-id="element-464" data-mp="3iqzhaicegbz6">
            {['home', 'office'].map(type => <button key={type} onClick={() => setApplyAddressType(type as any)} className={`flex-1 py-3 rounded-xl border-2 text-[13px] font-bold capitalize transition-colors ${applyAddressType === type ? isDark ? 'border-[#F37024] text-[#F37024] bg-[#F37024]/10' : 'border-[#F37024] text-[#F37024] bg-[#F37024]/5' : isDark ? 'border-[#3D2518] text-[#B8A89E]' : 'border-[#E5D5C5] text-[#818285]'}`} data-id="element-465" data-mp="3npkbo14z01fp">
                {type}
              </button>)}
          </div>
          <textarea value={applyAddress} onChange={e => setApplyAddress(e.target.value)} placeholder="Enter full delivery address..." rows={3} className={`w-full p-4 rounded-xl border-2 outline-none resize-none transition-colors ${isDark ? 'bg-[#2D1810] border-[#3D2518] text-white focus:border-[#F37024]' : 'bg-white border-[#E5D5C5] text-[#303030] focus:border-[#F37024]'}`} data-id="element-466" data-mp="3jvj4kz0dca2n" />
        </div>
      </div>
      <div className="px-6 mt-auto pt-6" data-id="element-467" data-mp="6mxydxj1x26n">
        <button onClick={() => navigateTo('apply_review')} disabled={!nameOnCard || !applyAddress} className="w-full py-4 bg-[#F37024] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-468" data-mp="134u9c6pyvuy0">
          Continue
        </button>
      </div>
    </motion.div>;
  const renderApplyReview = () => {
    const acc = PAY_FROM_ACCOUNTS.find(a => a.id === selectedApplyAccount);
    return <motion.div key="apply_review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-469" data-mp="29zccti9sc3io">
        <Header title="Review Application" onBackClick={() => navigateTo('apply_name_delivery', -1)} data-id="element-470" data-mp="2e30pugh6rrbb" />
        <div className="px-6 mt-4 flex-1 overflow-y-auto" data-id="element-471" data-mp="2srndr6kvpre5">
          <div className={`rounded-2xl p-5 mb-6 ${cardSurface}`} data-id="element-472" data-mp="2sr55tcoutoqe">
            <h3 className={`text-[16px] font-bold mb-4 ${textPri}`} data-id="element-473" data-mp="2ntv8lav848s0">
              Application Summary
            </h3>
            <div className="space-y-4" data-id="element-474" data-mp="31t4oe9fip0vg">
              <div className="flex justify-between" data-id="element-475" data-mp="2xr007q7u8wg7">
                <span className={`text-[13px] ${textSec}`} data-id="element-476" data-mp="2xskt7bei87by">Linked Account</span>
                <span className={`text-[13px] font-bold ${textPri}`} data-id="element-477" data-mp="3ggd6lu78u6gx">
                  {acc?.number}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-478" data-mp="3lcakltv1b0qj">
                <span className={`text-[13px] ${textSec}`} data-id="element-479" data-mp="37dkdrmiexmoz">Card Type</span>
                <span className={`text-[13px] font-bold ${textPri}`} data-id="element-480" data-mp="3cdgjbq6rdm6n">
                  {selectedApplyCard?.name}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-481" data-mp="2xnhonxeewsvx">
                <span className={`text-[13px] ${textSec}`} data-id="element-482" data-mp="32g2aocr2u1ow">Name on Card</span>
                <span className={`text-[13px] font-bold ${textPri}`} data-id="element-483" data-mp="2oo48klvs9628">
                  {nameOnCard}
                </span>
              </div>
              <div className="flex justify-between" data-id="element-484" data-mp="2sqitrcmxchwo">
                <span className={`text-[13px] ${textSec}`} data-id="element-485" data-mp="2epy90axg8c0j">Annual Charges</span>
                <span className={`text-[13px] font-bold ${textPri}`} data-id="element-486" data-mp="2es17zkgkxfhe">
                  {selectedApplyCard?.fee}
                </span>
              </div>
              <div className="pt-4 border-t border-black/5 dark:border-white/5" data-id="element-487" data-mp="p9umdx21c629">
                <span className={`block text-[13px] ${textSec} mb-1`} data-id="element-488" data-mp="khkpfx2i65qh">
                  Delivery Address ({applyAddressType})
                </span>
                <span className={`block text-[13px] font-bold ${textPri}`} data-id="element-489" data-mp="ggr51do9r92c">
                  {applyAddress}
                </span>
              </div>
            </div>
          </div>
          <label className="flex items-start gap-3 cursor-pointer" data-id="element-490" data-mp="1875aqmm4pf2x">
            <div className="mt-0.5" data-id="element-491" data-mp="145tsj25l3w4r">
              <div onClick={() => setApplyTermsAgreed(!applyTermsAgreed)} className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${applyTermsAgreed ? 'bg-[#F37024] border-[#F37024]' : isDark ? 'border-[#3D2518]' : 'border-[#E5D5C5]'}`} data-id="element-492" data-mp="146v4r9yc6oo2">
                {applyTermsAgreed && <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} data-id="element-493" data-mp="3jysqwqqkmkbi" />}
              </div>
            </div>
            <p className={`text-[13px] leading-relaxed ${textSec}`} data-id="element-494" data-mp="3f07r2sf8e2nl">
              I agree to the Terms & Conditions and authorize the bank to deduct
              the annual fee from my linked account.
            </p>
          </label>
        </div>
        <div className="px-6 mt-auto pt-6" data-id="element-495" data-mp="3es43qtlk8tcy">
          <button onClick={() => navigateTo('apply_processing')} disabled={!applyTermsAgreed} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-496" data-mp="3er2sop4jo6v8">
            Submit Application
          </button>
        </div>
      </motion.div>;
  };
  const renderApplyProcessing = () => <motion.div key="apply_processing" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col items-center justify-center ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-497" data-mp="1e93rspw5gjw">
      <motion.div animate={{
      rotate: 360
    }} transition={{
      duration: 1,
      repeat: Infinity,
      ease: 'linear'
    }} className="mb-6" data-id="element-498" data-mp="283feh9p92gfp">
        <RefreshCwIcon className="w-14 h-14 text-[#F37024]" strokeWidth={1.5} data-id="element-499" data-mp="2hrggy41ldzu" />
      </motion.div>
      <p className={`text-[20px] font-bold mb-2 ${textPri}`} data-id="element-500" data-mp="3to2bacigliiq">
        Processing Application...
      </p>
    </motion.div>;
  const renderApplySuccess = () => <motion.div key="apply_success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-501" data-mp="6h09g0r9d7uc">
      <SuccessView title="Application Submitted" subtitle="Your card application has been received." actionText="Back to Cards" onAction={() => navigateTo('tabs', -1)} data-id="element-502" data-mp="3ax1d3qp5xr08" />
    </motion.div>;
  // ===== Screens =====
  const renderTabs = () => <motion.div key="tabs" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-20 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-503" data-mp="3jubs9n53q0af">
      <div className="px-6 pt-6 pb-4 flex items-center justify-between" data-id="element-504" data-mp="16pggkqre9s3l">
        <button onClick={onBack} aria-label="Back" className={`p-2 -ml-2 ${textPri}`} data-id="element-505" data-mp="16o5dkt5rwodj">
          <ArrowLeftIcon className="w-6 h-6" strokeWidth={2} data-id="element-506" data-mp="1bhpzvny07tww" />
        </button>
        <h1 className={`text-[18px] font-bold ${textPri}`} data-id="element-507" data-mp="xptc1w3dc66b">My Cards</h1>
        <button onClick={onNavigateHome} aria-label="Home" className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${isDark ? 'border-[#F37024] text-[#F37024]' : 'border-[#5A1408] text-[#5A1408]'}`} data-id="element-508" data-mp="12nldbgpwl4n6">
          <HomeIcon className="w-5 h-5" strokeWidth={2} data-id="element-509" data-mp="1udzo342me0um" />
        </button>
      </div>

      <div className="px-6 mt-2" data-id="element-510" data-mp="1gfrn8l2gqfe6">
        <div className={`relative flex rounded-2xl p-1 ${isDark ? 'bg-[#2D1810]' : 'bg-[#F5EBE6]'}`} data-id="element-511" data-mp="1geq6q12oh0p2">
          <motion.div layout className="absolute top-1 bottom-1 rounded-xl bg-[#F37024] shadow-md" initial={false} animate={{
          left: tab === 'credit' ? 4 : '50%',
          right: tab === 'credit' ? '50%' : 4
        }} transition={{
          type: 'spring',
          damping: 28,
          stiffness: 280
        }} data-id="element-512" data-mp="1lcrtox385k5t" />
          <button onClick={() => setTab('credit')} className={`relative z-10 flex-1 py-2.5 text-[13px] font-bold rounded-xl transition-colors ${tab === 'credit' ? 'text-white' : isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-513" data-mp="3m4a7pltj5wjr">
            My Credit Cards
          </button>
          <button onClick={() => setTab('debit')} className={`relative z-10 flex-1 py-2.5 text-[13px] font-bold rounded-xl transition-colors ${tab === 'debit' ? 'text-white' : isDark ? 'text-[#B8A89E]' : 'text-[#818285]'}`} data-id="element-514" data-mp="38asm7vhaa9r3">
            My Debit Cards
          </button>
        </div>
      </div>

      <div className="px-6 mt-6" data-id="element-515" data-mp="2ubjhbryj322f">
        <button onClick={() => navigateTo('apply_account')} className={`w-full py-3.5 rounded-2xl flex items-center justify-center gap-2 border-2 border-dashed ${isDark ? 'border-[#F37024]/40 text-[#F37024] hover:bg-[#F37024]/10' : 'border-[#5A1408]/40 text-[#5A1408] hover:bg-[#5A1408]/5'} transition-colors`} data-id="element-516" data-mp="2pc6lihpoiknp">
          <PlusIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-517" data-mp="v4umcyxn2d81" />
          <span className="text-[14px] font-bold" data-id="element-518" data-mp="v28fz2mhi3re">Apply for a New Card</span>
        </button>
      </div>

      <div className="mt-6" data-id="element-519" data-mp="101cqbwl5a7jb">
        <div ref={carouselRef} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide py-4" style={{
        paddingLeft: '50px',
        paddingRight: '50px',
        gap: '5px'
      }} data-id="element-520" data-mp="14ue23vq21qyo">
          {CAROUSEL_CARDS.filter(c => c.type === tab).map((card, i) => {
          const isActive = carouselActiveIndex === i;
          const isCardActive = cardStatuses[card.id];
          return <div key={card.id} ref={el => cardRefs.current[i] = el} className="snap-center shrink-0 w-[85%] transition-all duration-350 ease-out relative" style={{
            transform: isActive ? 'scale(1)' : 'scale(0.88)',
            opacity: isActive ? 1 : 0.55
          }} data-id="element-521" data-mp="m3wgeb3n3vmt" data-mpi={i}>
                <div style={{
              opacity: isCardActive ? 1 : 0.55,
              transition: 'opacity 0.3s'
            }} data-id="element-522" data-mp="1xl5v6kbtpzhs">
                  {card.type === 'credit' ? <CreditCardVisual number={card.number} cardholder="KASHISH" expires={card.expires} data-id="element-523" data-mp="2nmgn1cjhxwyt" /> : <DebitCardVisual number={card.number} cardholder="KASHISH" expires={card.expires} tier={card.label} data-id="element-524" data-mp="2inx2ngkjwt8k" />}
                </div>
                {!isCardActive && !card.isNew && <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-525" data-mp="2ski3rqtg9k74">
                    <div className="bg-black/60 backdrop-blur-sm rounded-full p-3" data-id="element-526" data-mp="36bnafcv8jcqy">
                      <LockIcon className="w-7 h-7 text-white" strokeWidth={2.2} data-id="element-527" data-mp="31dlo8j2iixb9" />
                    </div>
                  </div>}
              </div>;
        })}
        </div>
      </div>

      {(() => {
      const activeCards = CAROUSEL_CARDS.filter(c => c.type === tab);
      const activeCard = activeCards[carouselActiveIndex];
      if (!activeCard) return null;
      const isCardActive = cardStatuses[activeCard.id];
      return <div className="px-6 mt-2" data-id="element-528" data-mp="1gyjrsz14x9pq">
            <div className={`flex flex-col gap-4 rounded-2xl px-4 py-4 ${cardSurface}`} data-id="element-529" data-mp="1gzl6rel9ugcq">
              <div className="flex items-center justify-between" data-id="element-530" data-mp="3sln8i5a1duew">
                <div className="flex items-center gap-3" data-id="element-531" data-mp="1diwwiralpaa">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
                background: isCardActive ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.10)'
              }} data-id="element-532" data-mp="3iv223vo4fllf">
                    {isCardActive ? <UnlockIcon className="w-5 h-5 text-[#10B981]" strokeWidth={2} data-id="element-533" data-mp="39u4908f80yhm" /> : <LockIcon className="w-5 h-5 text-[#EF4444]" strokeWidth={2} data-id="element-534" data-mp="39wqcnwyzqtuk" />}
                  </div>
                  <div data-id="element-535" data-mp="2zw6ckxn7jvdx">
                    <p className={`text-[14px] font-bold ${textPri}`} data-id="element-536" data-mp="2zwzkw31tesy6">
                      {isCardActive ? 'Active' : 'Inactive'}
                    </p>
                    <p className={`text-[11px] ${textSec}`} data-id="element-537" data-mp="34x42xxidfvqi">
                      {isCardActive ? 'Card is ready to use' : activeCard.inactiveReason || 'Card is locked'}
                    </p>
                  </div>
                </div>
                {!activeCard.isNew && <button onClick={() => setCardStatuses(prev => ({
              ...prev,
              [activeCard.id]: !prev[activeCard.id]
            }))} aria-pressed={isCardActive} className="relative w-12 h-7 rounded-full transition-colors" style={{
              background: isCardActive ? '#10B981' : '#EF4444'
            }} data-id="element-538" data-mp="2ym01c08er2bh">
                    <motion.div className="absolute top-1 w-5 h-5 rounded-full bg-white shadow-md" animate={{
                left: isCardActive ? 24 : 4
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 30
              }} data-id="element-539" data-mp="2ukxzdbn3wc5t" />
                  </button>}
              </div>
              {activeCard.isNew && !isCardActive && <button onClick={() => {
            setSelectedCard(CREDIT_CARD); // Reuse existing activation flow with mock card
            navigateTo('activation_details');
          }} className="w-full py-3 bg-[#F37024] text-white font-bold text-[14px] rounded-xl shadow-md active:scale-[0.98] transition-transform" data-id="element-540" data-mp="2avds45mo3bzw">
                  Activate Card
                </button>}
            </div>
          </div>;
    })()}

      {tab === 'credit' && <div className="px-6 mt-6 grid grid-cols-2 gap-3" data-id="element-541" data-mp="1s6k41jgtqsdq">
          <button onClick={() => {
        setSelectedCard(CREDIT_CARD);
        navigateTo('summary');
      }} disabled={isLocked} className={`rounded-2xl py-4 px-3 flex flex-col items-center gap-2 shadow-sm active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed ${cardSurface}`} data-id="element-542" data-mp="1s84xt6vbe7aj">
            <ReceiptIcon className="w-6 h-6 text-[#F37024]" strokeWidth={2} data-id="element-543" data-mp="1w6cek1ps38lq" />
            <span className={`text-[13px] font-bold ${textPri}`} data-id="element-544" data-mp="2aws2vpdhqwo4">
              Card Summary
            </span>
          </button>
          <button onClick={() => {
        setSelectedCard(CREDIT_CARD);
        navigateTo('manage_cards');
      }} disabled={isLocked} className={`rounded-2xl py-4 px-3 flex flex-col items-center gap-2 shadow-sm active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed ${cardSurface}`} data-id="element-545" data-mp="260kvrt9czf4l">
            <SettingsIcon className="w-6 h-6 text-[#F37024]" strokeWidth={2} data-id="element-546" data-mp="2ryfp4bootlzy" />
            <span className={`text-[13px] font-bold ${textPri}`} data-id="element-547" data-mp="2rwcoyyttmchi">
              Manage Card
            </span>
          </button>
        </div>}
      {tab === 'debit' && <div className="px-6 mt-6" data-id="element-548" data-mp="31pacxkhl0tni">
          <button onClick={() => {
        setSelectedCard(DEBIT_CARDS[0]);
        navigateTo('manage_cards');
      }} className={`w-full rounded-2xl py-4 px-3 flex items-center justify-center gap-2 shadow-sm active:scale-[0.98] transition-all ${cardSurface}`} data-id="element-549" data-mp="31qbr3xtw9w9g">
            <SettingsIcon className="w-6 h-6 text-[#F37024]" strokeWidth={2} data-id="element-550" data-mp="3fot8zex4cejg" />
            <span className={`text-[14px] font-bold ${textPri}`} data-id="element-551" data-mp="3kmbknd2it7v5">
              Manage Debit Cards
            </span>
          </button>
        </div>}
    </motion.div>;
  const renderSummary = () => {
    const utilization = (CREDIT_CARD.totalLimit - CREDIT_CARD.availableLimit) / CREDIT_CARD.totalLimit * 100;
    return <motion.div key="summary" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-552" data-mp="7f0psevb2j8b">
        <Header title="Card Summary" data-id="element-553" data-mp="3urtae7la55f3" />
        <div className="px-6 space-y-5" data-id="element-554" data-mp="3ps6yq2l3nx8e">
          <CreditCardVisual number={CREDIT_CARD.number} cardholder={CREDIT_CARD.cardholder} expires={CREDIT_CARD.expires} data-id="element-555" data-mp="3pt9mi1n4ljk3" />

          <div className={`rounded-2xl p-5 ${cardSurface}`} data-id="element-556" data-mp="qcaby3s0mat9">
            <div className="flex items-baseline justify-between mb-2" data-id="element-557" data-mp="mb8bjjmd3spp">
              <p className={`text-[12px] font-bold uppercase tracking-wider ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-558" data-mp="hcetrf01lfjq">
                Utilization
              </p>
              <p className={`text-[13px] font-bold ${textPri}`} data-id="element-559" data-mp="cfeov7exiiu7">
                {utilization.toFixed(0)}%
              </p>
            </div>
            <div className={`relative h-2.5 rounded-full overflow-hidden ${isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]'}`} data-id="element-560" data-mp="2x2zz109wt1lk">
              <motion.div initial={{
              width: 0
            }} animate={{
              width: `${utilization}%`
            }} transition={{
              duration: 0.8,
              ease: 'easeOut'
            }} className="absolute inset-y-0 left-0 rounded-full" style={{
              background: 'linear-gradient(90deg, #F37024 0%, #D95A1A 100%)'
            }} data-id="element-561" data-mp="ots4sp7qb23c" />
            </div>
            <div className="flex justify-between mt-3" data-id="element-562" data-mp="18jvlenjr5ibo">
              <div data-id="element-563" data-mp="1ckfffi9145tl">
                <p className={`text-[10px] uppercase tracking-wider ${textSec}`} data-id="element-564" data-mp="1ckxlf6lhtyep">
                  Used
                </p>
                <p className={`text-[13px] font-bold ${textPri}`} data-id="element-565" data-mp="yjuwmm346a03">
                  PKR{' '}
                  {formatPKR(CREDIT_CARD.totalLimit - CREDIT_CARD.availableLimit)}
                </p>
              </div>
              <div className="text-right" data-id="element-566" data-mp="3j9an1fyf7wp7">
                <p className={`text-[10px] uppercase tracking-wider ${textSec}`} data-id="element-567" data-mp="3o9ov2y00v6n8">
                  Available
                </p>
                <p className={`text-[13px] font-bold text-[#10B981]`} data-id="element-568" data-mp="3gpe92vuuqdec">
                  PKR {formatPKR(CREDIT_CARD.availableLimit)}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3" data-id="element-569" data-mp="3ppu9fwgat4ez">
            <div className={`rounded-2xl p-4 ${cardSurface}`} data-id="element-570" data-mp="3ks12v2b328e6">
              <p className={`text-[10px] uppercase tracking-wider ${textSec}`} data-id="element-571" data-mp="3kskek1a540jr">
                Total Amount Due
              </p>
              <p className={`text-[16px] font-bold mt-1 text-[#EF4444]`} data-id="element-572" data-mp="2t0v00e3z9wl7">
                PKR {formatPKR(CREDIT_CARD.totalDue)}
              </p>
            </div>
            <div className={`rounded-2xl p-4 ${cardSurface}`} data-id="element-573" data-mp="36tuc064jx8o0">
              <p className={`text-[10px] uppercase tracking-wider ${textSec}`} data-id="element-574" data-mp="321b0rftc6pmd">
                Minimum Amount Due
              </p>
              <p className={`text-[16px] font-bold mt-1 ${textPri}`} data-id="element-575" data-mp="2eae0x1etfirh">
                PKR {formatPKR(CREDIT_CARD.minDue)}
              </p>
            </div>
          </div>

          <button onClick={() => {
          setPayAmount('');
          navigateTo('pay_bill');
        }} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform flex items-center justify-center gap-2" data-id="element-576" data-mp="dair2rsfhusv">
            Pay Bill <ArrowRightIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-577" data-mp="rywub3trbl9y" />
          </button>

          <div data-id="element-578" data-mp="3qgizpgghd8wx">
            <h3 className={`text-[16px] font-bold mb-3 ${textPri}`} data-id="element-579" data-mp="3qh2i0ya1lzbh">
              Recent Transactions
            </h3>
            <div className={`rounded-2xl overflow-hidden ${cardSurface}`} data-id="element-580" data-mp="3vg5azkcr0f33">
              {TRANSACTIONS.map((tx, i) => <div key={tx.id} className={`p-4 flex items-center justify-between ${i !== TRANSACTIONS.length - 1 ? 'border-b border-[rgba(0,0,0,0.05)]' : ''}`} data-id="element-581" data-mp="47hov26oonuh" data-mpi={i}>
                  <div className="flex items-center gap-3" data-id="element-582" data-mp="1for93qxmzjwu">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]'}`} data-id="element-583" data-mp="1fqkel2wijy39">
                      {tx.type === 'credit' ? <ArrowLeftIcon className="w-5 h-5 text-[#10B981]" data-id="element-584" data-mp="346kb89re7ayq" /> : <ReceiptIcon className="w-5 h-5 text-[#F37024]" data-id="element-585" data-mp="306ihcmrwflu0" />}
                    </div>
                    <div data-id="element-586" data-mp="2vfa7xqqkk0gu">
                      <p className={`text-[14px] font-bold ${textPri}`} data-id="element-587" data-mp="2veqryfkhbm5g">
                        {tx.merchant}
                      </p>
                      <p className={`text-[11px] ${textSec}`} data-id="element-588" data-mp="2qg73o8iexy9x">{tx.date}</p>
                    </div>
                  </div>
                  <p className={`text-[14px] font-bold ${tx.type === 'credit' ? 'text-[#10B981]' : textPri}`} data-id="element-589" data-mp="2gi9e9hothvfq">
                    {tx.type === 'credit' ? '+' : '-'} PKR{' '}
                    {formatPKR(tx.amount)}
                  </p>
                </div>)}
            </div>
          </div>
        </div>
      </motion.div>;
  };
  const renderManageCards = () => <motion.div key="manage_cards" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-590" data-mp="anzwlijzqlnn">
      <Header title="Manage Cards" data-id="element-591" data-mp="3aqrxmo2nezov" />
      <div className="px-6 space-y-6" data-id="element-592" data-mp="3fpcz0qtmzpew">
        <div data-id="element-593" data-mp="3fobgk1aak0n5">
          <h3 className={`text-[14px] font-bold uppercase tracking-wider mb-3 ${textSec}`} data-id="element-594" data-mp="3fotk7iza9h53">
            Physical Cards
          </h3>
          <div className="space-y-3" data-id="element-595" data-mp="30ym6fgk80maq">
            {[CREDIT_CARD, ...DEBIT_CARDS].map(card => <button key={card.id} onClick={() => {
            setSelectedCard(card);
            navigateTo('card_detail');
          }} className={`w-full p-4 rounded-2xl flex items-center justify-between active:scale-[0.98] transition-transform ${cardSurface}`} data-id="element-596" data-mp="35xgr6o1r0kxh">
                <div className="flex items-center gap-3" data-id="element-597" data-mp="2i2w5lwo0xvyf">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]'}`} data-id="element-598" data-mp="2n0fiot8zk2os">
                    <CreditCardIcon className="w-6 h-6 text-[#F37024]" data-id="element-599" data-mp="gbj31e9j405j" />
                  </div>
                  <div className="text-left" data-id="element-600" data-mp="l89ibzdierpw">
                    <p className={`text-[14px] font-bold ${textPri}`} data-id="element-601" data-mp="l6yddw8bvdx7">
                      {card.type} • {card.tier}
                    </p>
                    <p className={`text-[12px] ${textSec}`} data-id="element-602" data-mp="20t1rdy993wi0">{card.number}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-603" data-mp="2fipqcstymj2k">
                  <span className={`px-2 py-1 rounded-md text-[10px] font-bold ${card.status === 'Active' ? 'bg-[#10B981]/10 text-[#10B981]' : 'bg-[#EF4444]/10 text-[#EF4444]'}`} data-id="element-604" data-mp="2apwme76bferj">
                    {card.status}
                  </span>
                  <ChevronRightIcon className={`w-5 h-5 ${textSec}`} data-id="element-605" data-mp="2ji8bnppp0w5x" />
                </div>
              </button>)}
          </div>
        </div>

        <div data-id="element-606" data-mp="2tgxtxjli4imp">
          <h3 className={`text-[14px] font-bold uppercase tracking-wider mb-3 ${textSec}`} data-id="element-607" data-mp="2tgece3zrjw97">
            Virtual Cards
          </h3>
          <div className="space-y-3" data-id="element-608" data-mp="142srocwbkks5">
            {VIRTUAL_CARDS.map(card => <button key={card.id} onClick={() => {
            setSelectedCard(card);
            navigateTo('card_detail');
          }} className={`w-full p-4 rounded-2xl flex items-center justify-between active:scale-[0.98] transition-transform ${cardSurface}`} data-id="element-609" data-mp="z3g39npsv5ng">
                <div className="flex items-center gap-3" data-id="element-610" data-mp="2ovn8afjymcnt">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]'}`} data-id="element-611" data-mp="2k19jivzsz3s4">
                    <SmartphoneIcon className="w-6 h-6 text-[#F37024]" data-id="element-612" data-mp="2fz66w41edt59" />
                  </div>
                  <div className="text-left" data-id="element-613" data-mp="2azt94o8zn1nw">
                    <p className={`text-[14px] font-bold ${textPri}`} data-id="element-614" data-mp="262jl9d18yy26">
                      {card.type} • {card.tier}
                    </p>
                    <p className={`text-[12px] ${textSec}`} data-id="element-615" data-mp="213zwz5z6la6n">{card.number}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-616" data-mp="3vhr5g25rxo0i">
                  <span className="px-2 py-1 rounded-md text-[10px] font-bold bg-[#10B981]/10 text-[#10B981]" data-id="element-617" data-mp="3vh7mci4e0tkw">
                    {card.status}
                  </span>
                  <ChevronRightIcon className={`w-5 h-5 ${textSec}`} data-id="element-618" data-mp="1d96e8ono34kd" />
                </div>
              </button>)}
          </div>
        </div>
      </div>
    </motion.div>;
  const renderCardDetail = () => <motion.div key="card_detail" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-619" data-mp="1mxxzkdy82qmb">
      <Header title="Card Details" onBackClick={() => navigateTo('manage_cards', -1)} data-id="element-620" data-mp="uakbz6gez7ev" />
      <div className="px-6 space-y-6" data-id="element-621" data-mp="z7tx49wrswww">
        {selectedCard.type === 'CREDIT' && <CreditCardVisual {...selectedCard} showFull={showVirtualDetails} data-id="element-622" data-mp="13a72uybiouto" />}
        {selectedCard.type === 'DEBIT' && <DebitCardVisual {...selectedCard} showFull={showVirtualDetails} data-id="element-623" data-mp="1891rwi02vfm8" />}
        {selectedCard.type === 'VIRTUAL' && <VirtualCardVisual {...selectedCard} showFull={showVirtualDetails} data-id="element-624" data-mp="2eq5glfhn4518" />}

        {selectedCard.type === 'VIRTUAL' && <button onClick={() => setShowVirtualDetails(!showVirtualDetails)} className={`w-full py-3 rounded-xl flex items-center justify-center gap-2 font-bold ${cardSurface} ${textPri}`} data-id="element-625" data-mp="2jnfgxq6ki13u">
            {showVirtualDetails ? <EyeOffIcon className="w-5 h-5" data-id="element-626" data-mp="fk2p5ai0xsh1" /> : <EyeIcon className="w-5 h-5" data-id="element-627" data-mp="fmyq77p64x9p" />}
            {showVirtualDetails ? 'Hide Details' : 'Reveal Details'}
          </button>}

        <div className="grid grid-cols-4 gap-3" data-id="element-628" data-mp="ol0v3yc0wa29">
          <button onClick={() => navigateTo('card_controls')} className={`p-3 rounded-2xl flex flex-col items-center justify-center gap-2 ${cardSurface}`} data-id="element-629" data-mp="oj7mwelrhhs5">
            <SlidersIcon className="w-6 h-6 text-[#F37024]" data-id="element-630" data-mp="3s2sjz3uv4dw8" />
            <span className={`text-[11px] font-bold text-center ${textPri}`} data-id="element-631" data-mp="alj0h2c1q2kz">
              Controls
            </span>
          </button>
          <button onClick={() => navigateTo('spending_limits')} className={`p-3 rounded-2xl flex flex-col items-center justify-center gap-2 ${cardSurface}`} data-id="element-632" data-mp="5trl7nycbja5">
            <ShieldIcon className="w-6 h-6 text-[#F37024]" data-id="element-633" data-mp="39bj7clpsp90f" />
            <span className={`text-[11px] font-bold text-center ${textPri}`} data-id="element-634" data-mp="39azzxyxru30n">
              Limits
            </span>
          </button>
          <button onClick={() => {
          setPinInput('');
          navigateTo('pin_management');
        }} className={`p-3 rounded-2xl flex flex-col items-center justify-center gap-2 ${cardSurface}`} data-id="element-635" data-mp="1tk76tzcrgt55">
            <LockIcon className="w-6 h-6 text-[#F37024]" data-id="element-636" data-mp="1judyd77x0u54" />
            <span className={cn(`text-[11px] font-bold text-center ${textPri}`, 'pb-[0px] mb-[0px]')} data-id="element-637" data-mp="1juxhgr9axokq">
              Change PIN
            </span>
          </button>
          <button onClick={() => navigateTo('block_entry')} className={`p-3 rounded-2xl flex flex-col items-center justify-center gap-2 ${cardSurface}`} data-id="element-638" data-mp="2cjbfv84aymwt">
            <AlertCircleIcon className="w-6 h-6 text-[#EF4444]" data-id="element-639" data-mp="1ylms9siup1o2" />
            <span className={`text-[11px] font-bold text-center ${textPri}`} data-id="element-640" data-mp="23h27anslh3m2">
              Block
            </span>
          </button>
        </div>

        <div className={`rounded-2xl overflow-hidden ${cardSurface}`} data-id="element-641" data-mp="s3806wbr96ih">
          <button onClick={() => navigateTo('activation_entry')} className="w-full p-4 flex items-center justify-between" data-id="element-642" data-mp="s152rr89wb3q">
            <div className="flex items-center gap-3" data-id="element-643" data-mp="12m4vrj4ayag4">
              <CheckIcon className={`w-5 h-5 ${textSec}`} data-id="element-644" data-mp="12klepqatnvda" />
              <span className={`text-[14px] font-bold ${textPri}`} data-id="element-645" data-mp="xmjuh21ntq08">
                Activate Card
              </span>
            </div>
            <ChevronRightIcon className={`w-5 h-5 ${textSec}`} data-id="element-646" data-mp="tn1b4aphlmzi" />
          </button>
        </div>
      </div>
    </motion.div>;
  const renderCardControls = () => <motion.div key="card_controls" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-647" data-mp="1lcw2ecwwnmqt">
      <Header title="Card Controls" onBackClick={() => navigateTo('card_detail', -1)} data-id="element-648" data-mp="1cchdz4on9rj3" />
      <div className="px-6 space-y-4" data-id="element-649" data-mp="5vm6uce5gvlm">
        {[{
        id: 'ecommerce',
        label: 'E-Commerce',
        desc: 'Online purchases',
        icon: GlobeIcon
      }, {
        id: 'contactless',
        label: 'Contactless',
        desc: 'Tap to pay',
        icon: WifiIcon
      }, {
        id: 'international',
        label: 'International',
        desc: 'Use abroad',
        icon: MapPinIcon
      }, {
        id: 'atm',
        label: 'ATM Withdrawals',
        desc: 'Cash from ATMs',
        icon: CreditCardIcon
      }].map(ctrl => <div key={ctrl.id} className={`p-4 rounded-2xl flex items-center justify-between ${cardSurface}`} data-id="element-650" data-mp="2qd40hek40eqq">
            <div className="flex items-center gap-3" data-id="element-651" data-mp="2vadznlxaw4rr">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]'}`} data-id="element-652" data-mp="308xllmbw9gk4">
                <ctrl.icon className="w-5 h-5 text-[#F37024]" data-id="element-653" data-mp="359bhk608101s" />
              </div>
              <div data-id="element-654" data-mp="399djmg9vbvhl">
                <p className={`text-[14px] font-bold ${textPri}`} data-id="element-655" data-mp="399m0ej943ky2">
                  {ctrl.label}
                </p>
                <p className={`text-[11px] ${textSec}`} data-id="element-656" data-mp="1awvdnbuwdy03">{ctrl.desc}</p>
              </div>
            </div>
            <button onClick={() => {
          setPendingToggle(ctrl.id);
          navigateTo('toggle_confirm');
        }} className="relative w-12 h-7 rounded-full transition-colors" style={{
          background: toggles[ctrl.id as keyof typeof toggles] ? '#10B981' : '#EF4444'
        }} data-id="element-657" data-mp="1ey77jtkt15e3">
              <motion.div className="absolute top-1 w-5 h-5 rounded-full bg-white shadow-md" animate={{
            left: toggles[ctrl.id as keyof typeof toggles] ? 24 : 4
          }} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} data-id="element-658" data-mp="2r5n1mc54xesp" />
            </button>
          </div>)}
      </div>
    </motion.div>;
  const renderToggleConfirm = () => <motion.div key="toggle_confirm" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-659" data-mp="3ay1ql1x7dloc">
      <Header title="Confirm Change" onBackClick={() => navigateTo('card_controls', -1)} data-id="element-660" data-mp="3exk9js5grmoj" />
      <div className="px-6 flex-1 flex flex-col justify-center items-center text-center" data-id="element-661" data-mp="2dyfsk0y63e5">
        <AlertCircleIcon className="w-16 h-16 text-[#F37024] mb-4" data-id="element-662" data-mp="2c576z6rx93i" />
        <h2 className={`text-[20px] font-bold mb-2 ${textPri}`} data-id="element-663" data-mp="7cj4pnar10na">
          Are you sure?
        </h2>
        <p className={`text-[14px] mb-8 ${textSec}`} data-id="element-664" data-mp="3pi4g6xh3d79c">
          You are about to{' '}
          {toggles[pendingToggle as keyof typeof toggles] ? 'disable' : 'enable'}{' '}
          {pendingToggle} transactions for this card.
        </p>
        <div className="w-full space-y-3" data-id="element-665" data-mp="3kjkvt1i5c3j3">
          <button onClick={() => {
          setToggles(prev => ({
            ...prev,
            [pendingToggle!]: !prev[pendingToggle as keyof typeof toggles]
          }));
          navigateTo('toggle_success');
        }} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-666" data-mp="3kkcs6ejki5af">
            Confirm
          </button>
          <button onClick={() => navigateTo('card_controls', -1)} className={`w-full py-4 font-bold text-[16px] rounded-2xl ${textPri}`} data-id="element-667" data-mp="31rs3786zokbw">
            Cancel
          </button>
        </div>
      </div>
    </motion.div>;
  const BLOCK_REASONS = ['Lost', 'Stolen', 'Damaged', 'Fraudulent activity', 'Other'];
  const renderBlockEntry = () => <motion.div key="block_entry" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-668" data-mp="37ezzlsrfi9dr">
      <Header title="Block Card" onBackClick={() => navigateTo('card_detail', -1)} data-id="element-669" data-mp="2tlsac7qtlpz8" />
      <div className="px-6 flex-1 flex flex-col" data-id="element-670" data-mp="2tjp9eso4qcfq">
        <div className={`p-5 rounded-2xl mb-6 ${cardSurface}`} data-id="element-671" data-mp="2ongxdo58azcs">
          <div className="flex items-center gap-3" data-id="element-672" data-mp="2jnmb5xweexy2">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]'}`} data-id="element-673" data-mp="2joxanlit9jjd">
              <CreditCardIcon className="w-6 h-6 text-[#F37024]" data-id="element-674" data-mp="2amo416qnyab9" />
            </div>
            <div data-id="element-675" data-mp="2aor1gbu5b5q0">
              <p className={`text-[14px] font-bold ${textPri}`} data-id="element-676" data-mp="q4ly5757drla">
                {selectedCard?.type} • {selectedCard?.tier}
              </p>
              <p className={`text-[12px] ${textSec}`} data-id="element-677" data-mp="v2dpo01xrboy">{selectedCard?.number}</p>
            </div>
          </div>
        </div>
        <div className={`p-4 rounded-2xl flex items-start gap-3 mb-6 ${isDark ? 'bg-[#3D1A12]' : 'bg-[#FEF2F2]'}`} data-id="element-678" data-mp="z639kc7o153n">
          <AlertCircleIcon className="w-6 h-6 text-[#EF4444] shrink-0" data-id="element-679" data-mp="1420r2luo0hi0" />
          <p className={`text-[13px] ${textSec}`} data-id="element-680" data-mp="7fp0queieiyu">
            Blocking this card will immediately stop all transactions. You can
            request a replacement afterwards.
          </p>
        </div>
        <div className="mt-auto" data-id="element-681" data-mp="gdhjk8jfw8qz">
          <button onClick={() => navigateTo('block_reason')} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-682" data-mp="l5ivftcp3xgh">
            Continue
          </button>
        </div>
      </div>
    </motion.div>;
  const renderBlockReason = () => <motion.div key="block_reason" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-683" data-mp="2w9fu712xuiby">
      <Header title="Reason for Blocking" onBackClick={() => navigateTo('block_entry', -1)} data-id="element-684" data-mp="359koc9j4fk86" />
      <div className="px-6 space-y-3" data-id="element-685" data-mp="3a976mxdt3qnu">
        {BLOCK_REASONS.map(reason => <button key={reason} onClick={() => {
        setBlockReason(reason);
        navigateTo('block_confirm');
      }} className={`w-full p-4 rounded-2xl flex items-center justify-between active:scale-[0.98] transition-transform ${cardSurface}`} data-id="element-686" data-mp="2cl6u84v5l81w">
            <span className={`text-[14px] font-bold ${textPri}`} data-id="element-687" data-mp="2mhrygnzcmf4o">{reason}</span>
            <ChevronRightIcon className={`w-5 h-5 ${textSec}`} data-id="element-688" data-mp="2rb4lwusakfvu" />
          </button>)}
      </div>
    </motion.div>;
  const renderBlockConfirm = () => <motion.div key="block_confirm" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-689" data-mp="1tskzjnvzfniw">
      <Header title="Confirm Block" onBackClick={() => navigateTo('block_reason', -1)} data-id="element-690" data-mp="1mb6b38yqzgxx" />
      <div className="px-6 flex-1 flex flex-col justify-center items-center text-center" data-id="element-691" data-mp="1m9bnto007cqo">
        <AlertCircleIcon className="w-16 h-16 text-[#EF4444] mb-4" data-id="element-692" data-mp="1hdwg7dslad2p" />
        <h2 className={`text-[20px] font-bold mb-2 ${textPri}`} data-id="element-693" data-mp="1ccgudjil82j9">
          Block this card?
        </h2>
        <p className={`text-[14px] mb-8 ${textSec}`} data-id="element-694" data-mp="18cy8akf15leu">
          {selectedCard?.type} • {selectedCard?.tier} will be blocked
          {blockReason ? ` due to: ${blockReason}` : ''}. This cannot be undone.
        </p>
        <div className="w-full space-y-3" data-id="element-695" data-mp="yjhgtn2d8iec">
          <button onClick={() => navigateTo('block_success')} className="w-full py-4 bg-[#EF4444] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-696" data-mp="tna7dkargorn">
            Block Card
          </button>
          <button onClick={() => navigateTo('card_detail', -1)} className={`w-full py-4 font-bold text-[16px] rounded-2xl ${textPri}`} data-id="element-697" data-mp="kmcarn2njjj3">
            Cancel
          </button>
        </div>
      </div>
    </motion.div>;
  const renderSpendingLimits = () => <motion.div key="spending_limits" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-698" data-mp="10akwqiblh5d">
      <Header title="Spending Limits" onBackClick={() => navigateTo('card_detail', -1)} data-id="element-699" data-mp="tog2x5hzos1i" />
      <div className="px-6 space-y-6" data-id="element-700" data-mp="xqt91v0neryt">
        <div className={`p-5 rounded-2xl ${cardSurface}`} data-id="element-701" data-mp="xprwtn7wbzfi">
          <div className="flex justify-between mb-4" data-id="element-702" data-mp="jzeqfpankssa">
            <p className={`text-[14px] font-bold ${textPri}`} data-id="element-703" data-mp="jwsi3nfxu990">
              Daily ATM Limit
            </p>
            <p className={`text-[14px] font-bold text-[#F37024]`} data-id="element-704" data-mp="ovmykiqfnlpy">PKR 50,000</p>
          </div>
          <input type="range" min="0" max="100000" step="5000" defaultValue="50000" className="w-full accent-[#F37024]" data-id="element-705" data-mp="1clhb0z6ub89o" />
          <div className="flex justify-between mt-2" data-id="element-706" data-mp="1qt2n5ddt3uto">
            <span className={`text-[10px] ${textSec}`} data-id="element-707" data-mp="1qrjarxv9u3x6">0</span>
            <span className={`text-[10px] ${textSec}`} data-id="element-708" data-mp="1ms0mcs48rapl">100,000</span>
          </div>
        </div>
        <div className={`p-5 rounded-2xl ${cardSurface}`} data-id="element-709" data-mp="20jf7b4hpllza">
          <div className="flex justify-between mb-4" data-id="element-710" data-mp="20l06j7eyxt3h">
            <p className={`text-[14px] font-bold ${textPri}`} data-id="element-711" data-mp="1vku8n9i3vacy">
              Daily POS Limit
            </p>
            <p className={`text-[14px] font-bold text-[#F37024]`} data-id="element-712" data-mp="17uzwyv9ivrua">
              PKR 150,000
            </p>
          </div>
          <input type="range" min="0" max="500000" step="10000" defaultValue="150000" className="w-full accent-[#F37024]" data-id="element-713" data-mp="13ug1rx8ieyas" />
          <div className="flex justify-between mt-2" data-id="element-714" data-mp="1ctb6t5zti2ci">
            <span className={`text-[10px] ${textSec}`} data-id="element-715" data-mp="1crqabaty09c0">0</span>
            <span className={`text-[10px] ${textSec}`} data-id="element-716" data-mp="2t71lt7sh6b0x">500,000</span>
          </div>
        </div>
        <button onClick={() => navigateTo('card_detail', -1)} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-717" data-mp="2o8zrtrtigrao">
          Save Changes
        </button>
      </div>
    </motion.div>;
  const renderBlockedMerchants = () => <motion.div key="blocked_merchants" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-718" data-mp="17p0xc6tzdsh6">
      <Header title="Blocked Merchants" onBackClick={() => navigateTo('card_detail', -1)} data-id="element-719" data-mp="1gr0dkwx68sj2" />
      <div className="px-6 space-y-4" data-id="element-720" data-mp="1lojrtwtvd5b0">
        {BLOCKED_MERCHANTS.map(m => <div key={m.id} className={`p-4 rounded-2xl flex items-center justify-between ${cardSurface}`} data-id="element-721" data-mp="1lmz0eg2uq2hd">
            <div data-id="element-722" data-mp="1vf4x2341t82w">
              <p className={`text-[14px] font-bold ${textPri}`} data-id="element-723" data-mp="1vfmrcu75hc86">{m.name}</p>
              <p className={`text-[11px] ${textSec}`} data-id="element-724" data-mp="1zgq9jnco7kck">Blocked on {m.date}</p>
            </div>
            <button className="px-4 py-2 rounded-xl bg-[#10B981]/10 text-[#10B981] text-[12px] font-bold" data-id="element-725" data-mp="1zevo87xhlq7y">
              Unblock
            </button>
          </div>)}
      </div>
    </motion.div>;
  const renderPinManagement = () => <motion.div key="pin_management" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-726" data-mp="3jy94wrat8gyj">
      <Header title="PIN Management" onBackClick={() => navigateTo('card_detail', -1)} data-id="element-727" data-mp="3azdzhhflbawa" />
      <PinEntry title="Enter Current PIN" subtitle="Please enter your current 4-digit card PIN" onComplete={() => {
      setPinInput('');
      navigateTo('pin_enter_new');
    }} data-id="element-728" data-mp="bhw5otj08d2e" />
    </motion.div>;
  const renderPinEnterNew = () => <motion.div key="pin_enter_new" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-729" data-mp="2osxazn7uqem">
      <Header title="New PIN" onBackClick={() => {
      setPinInput('');
      navigateTo('pin_management', -1);
    }} data-id="element-730" data-mp="v8rj260d2x2l" />
      <PinEntry title="Enter New PIN" subtitle="Create a new 4-digit PIN for your card" onComplete={() => {
      setPinInput('');
      navigateTo('pin_confirm_new');
    }} data-id="element-731" data-mp="19byj65x7k1c3" />
    </motion.div>;
  const renderPinConfirmNew = () => <motion.div key="pin_confirm_new" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-732" data-mp="10j3jrsuf8zu8">
      <Header title="Confirm PIN" onBackClick={() => {
      setPinInput('');
      navigateTo('pin_enter_new', -1);
    }} data-id="element-733" data-mp="qknvryqoy6oy" />
      <PinEntry title="Confirm New PIN" subtitle="Re-enter your new 4-digit PIN" onComplete={() => navigateTo('pin_success')} data-id="element-734" data-mp="hlajyzf3h612" />
    </motion.div>;
  const renderActivationEntry = () => <motion.div key="activation_entry" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-735" data-mp="loysbkbjw5nd">
      <Header title="Activate Card" onBackClick={() => navigateTo('card_detail', -1)} data-id="element-736" data-mp="4dac9px774sq" />
      <div className="px-6 flex-1 flex flex-col justify-center items-center text-center" data-id="element-737" data-mp="4b7fmn1jidf1">
        <CreditCardIcon className="w-16 h-16 text-[#F37024] mb-4" data-id="element-738" data-mp="a5a5oh1554m" />
        <h2 className={`text-[20px] font-bold mb-2 ${textPri}`} data-id="element-739" data-mp="e4xxh935vplr">
          Activate Your Card
        </h2>
        <p className={`text-[14px] mb-8 ${textSec}`} data-id="element-740" data-mp="9b3ds5nk3amo">
          You'll need your physical card to complete this process.
        </p>
        <button onClick={() => navigateTo('activation_details')} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-741" data-mp="j2g9x9ge0myi">
          Start Activation
        </button>
      </div>
    </motion.div>;
  const renderActivationDetails = () => <motion.div key="activation_details" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-742" data-mp="2ytc7rk2lhzma">
      <Header title="Card Details" onBackClick={() => navigateTo('activation_entry', -1)} data-id="element-743" data-mp="28xs1egl2yrrc" />
      <div className="px-6 space-y-4 mt-4" data-id="element-744" data-mp="2czn2bjz3n34l">
        <div className={`p-4 rounded-2xl ${cardSurface}`} data-id="element-745" data-mp="2cylml2751sgj">
          <label className={`text-[12px] font-bold uppercase tracking-wider ${textSec}`} data-id="element-746" data-mp="2hs6ecci51q7a">
            Expiry Date (MM/YY)
          </label>
          <input type="text" placeholder="MM/YY" className={`w-full mt-2 bg-transparent text-[16px] font-bold outline-none ${textPri}`} data-id="element-747" data-mp="2mscaa4vfxyv6" />
        </div>
        <div className={`p-4 rounded-2xl ${cardSurface}`} data-id="element-748" data-mp="1u0sms8rt9ynd">
          <label className={`text-[12px] font-bold uppercase tracking-wider ${textSec}`} data-id="element-749" data-mp="1u3dpezzxoim6">
            CVV
          </label>
          <input type="password" placeholder="***" maxLength={3} className={`w-full mt-2 bg-transparent text-[16px] font-bold outline-none ${textPri}`} data-id="element-750" data-mp="23zgoty3ei35f" />
        </div>
        <button onClick={() => {
        setPinInput('');
        navigateTo('activation_set_pin');
      }} className="w-full py-4 mt-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-751" data-mp="3ahwuer3bz261">
          Next
        </button>
      </div>
    </motion.div>;
  const renderActivationSetPin = () => <motion.div key="activation_set_pin" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-752" data-mp="u0yvymydwckf">
      <Header title="Set PIN" onBackClick={() => navigateTo('activation_details', -1)} data-id="element-753" data-mp="l2n82sowjsvo" />
      <PinEntry title="Set Card PIN" subtitle="Create a 4-digit PIN for your new card" onComplete={() => navigateTo('activation_success')} data-id="element-754" data-mp="g168d0i5mcgo" />
    </motion.div>;
  const renderRequestEntry = () => <motion.div key="request_entry" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-755" data-mp="64uuw76tvgkn">
      <Header title="Request Card" onBackClick={() => navigateTo('manage_cards', -1)} data-id="element-756" data-mp="20ic8b1diqx1y" />
      <div className="px-6 space-y-4 mt-4" data-id="element-757" data-mp="2s6x9zib34cwx">
        <button onClick={() => navigateTo('request_type')} className={`w-full p-5 rounded-2xl flex items-center justify-between text-left ${cardSurface}`} data-id="element-758" data-mp="2n958bwkn1cfj">
          <div data-id="element-759" data-mp="31wqddw12plq3">
            <p className={`text-[16px] font-bold ${textPri}`} data-id="element-760" data-mp="31x9r10joxnyb">Debit Card</p>
            <p className={`text-[12px] mt-1 ${textSec}`} data-id="element-761" data-mp="2x09kypmucl77">
              Get a new physical debit card
            </p>
          </div>
          <ChevronRightIcon className={`w-5 h-5 ${textSec}`} data-id="element-762" data-mp="29bgpsve1mhdn" />
        </button>
        <button onClick={() => navigateTo('request_type')} className={`w-full p-5 rounded-2xl flex items-center justify-between text-left ${cardSurface}`} data-id="element-763" data-mp="24buckly7t14u">
          <div data-id="element-764" data-mp="2e7xlqbrhi01a">
            <p className={`text-[16px] font-bold ${textPri}`} data-id="element-765" data-mp="2e7ffqnf0s7g6">Virtual Card</p>
            <p className={`text-[12px] mt-1 ${textSec}`} data-id="element-766" data-mp="3um7a35vq7os4">
              Instantly generate a virtual card
            </p>
          </div>
          <ChevronRightIcon className={`w-5 h-5 ${textSec}`} data-id="element-767" data-mp="3pn451lfqxik5" />
        </button>
      </div>
    </motion.div>;
  const renderRequestType = () => <motion.div key="request_type" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-768" data-mp="143lvm2maid8">
      <Header title="Select Tier" onBackClick={() => navigateTo('request_entry', -1)} data-id="element-769" data-mp="3s7ikbgku469s" />
      <div className="px-6 space-y-4 mt-4" data-id="element-770" data-mp="jsig0i88eci9">
        <button onClick={() => navigateTo('request_delivery')} className={`w-full p-5 rounded-2xl flex items-center justify-between text-left border-2 border-[#F37024] ${isDark ? 'bg-[#3D2518]' : 'bg-[#FFF7F0]'}`} data-id="element-771" data-mp="jtju6vkjnf47">
          <div data-id="element-772" data-mp="azolxeo4l8kc">
            <p className={`text-[16px] font-bold ${textPri}`} data-id="element-773" data-mp="ay414grm53zo">Platinum</p>
            <p className={`text-[12px] mt-1 ${textSec}`} data-id="element-774" data-mp="ezyyxbac4z8p">
              Higher limits, premium perks
            </p>
          </div>
          <CheckIcon className="w-5 h-5 text-[#F37024]" data-id="element-775" data-mp="17ht7f93oyujq" />
        </button>
        <button onClick={() => navigateTo('request_delivery')} className={`w-full p-5 rounded-2xl flex items-center justify-between text-left ${cardSurface}`} data-id="element-776" data-mp="17j4bzb4yo6bw">
          <div data-id="element-777" data-mp="1dbybskgv1la1">
            <p className={`text-[16px] font-bold ${textPri}`} data-id="element-778" data-mp="1ddj92huk7ibl">Gold</p>
            <p className={`text-[12px] mt-1 ${textSec}`} data-id="element-779" data-mp="18clqi45n0m9k">
              Standard limits and features
            </p>
          </div>
        </button>
      </div>
    </motion.div>;
  const renderRequestDelivery = () => <motion.div key="request_delivery" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-780" data-mp="yf7fwl6earp6">
      <Header title="Delivery Details" onBackClick={() => navigateTo('request_type', -1)} data-id="element-781" data-mp="pn5cqf4xyxb7" />
      <div className="px-6 space-y-4 mt-4" data-id="element-782" data-mp="kou7yiti7czu">
        <div className={`p-4 rounded-2xl ${cardSurface}`} data-id="element-783" data-mp="fog5rhel2x9q">
          <p className={`text-[12px] font-bold uppercase tracking-wider ${textSec} mb-2`} data-id="element-784" data-mp="fny4u7gz7uvh">
            Delivery Address
          </p>
          <p className={`text-[14px] font-bold ${textPri}`} data-id="element-785" data-mp="lg5wt62meg7">
            123 Main Street, Phase 5, DHA, Lahore
          </p>
        </div>
        <button onClick={() => navigateTo('request_review')} className="w-full py-4 mt-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-786" data-mp="5jhntarrg7q3">
          Continue to Review
        </button>
      </div>
    </motion.div>;
  const renderRequestReview = () => <motion.div key="request_review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-787" data-mp="3e1ds02zrv08p">
      <Header title="Review Request" onBackClick={() => navigateTo('request_delivery', -1)} data-id="element-788" data-mp="3nx7cq9nna01k" />
      <div className="px-6 space-y-4 mt-4" data-id="element-789" data-mp="3sqs61oeam5uf">
        <div className={`p-5 rounded-2xl space-y-4 ${cardSurface}`} data-id="element-790" data-mp="2va32nq9p2vix">
          <div className="flex justify-between" data-id="element-791" data-mp="2v7qntlz8h1a3">
            <span className={`text-[13px] ${textSec}`} data-id="element-792" data-mp="3084lqb74euue">Card Type</span>
            <span className={`text-[13px] font-bold ${textPri}`} data-id="element-793" data-mp="15spicobcbjej">
              Platinum Debit
            </span>
          </div>
          <div className="flex justify-between" data-id="element-794" data-mp="10ue8idl1p8wb">
            <span className={`text-[13px] ${textSec}`} data-id="element-795" data-mp="10rjogn5y1q3f">Issuance Fee</span>
            <span className={`text-[13px] font-bold ${textPri}`} data-id="element-796" data-mp="1fk3jt1muqtab">
              PKR 1,500
            </span>
          </div>
          <div className="flex justify-between" data-id="element-797" data-mp="1appp71g0kq6p">
            <span className={`text-[13px] ${textSec}`} data-id="element-798" data-mp="1oi5te5kniu97">Delivery Address</span>
            <span className={`text-[13px] font-bold text-right w-1/2 ${textPri}`} data-id="element-799" data-mp="1khvl2m9dzrr6">
              123 Main Street, Phase 5, DHA, Lahore
            </span>
          </div>
        </div>
        <button onClick={() => navigateTo('request_processing')} className="w-full py-4 mt-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-800" data-mp="1tiab1yxapv10">
          Confirm Request
        </button>
      </div>
    </motion.div>;
  // Pay Bill Screens (reusing existing logic but condensed)
  const renderPayBill = () => {
    const numericAmount = Number(payAmount.replace(/[^0-9]/g, '')) || 0;
    const isValid = numericAmount >= CREDIT_CARD.minDue && numericAmount <= payFrom.balance;
    return <motion.div key="pay_bill" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-801" data-mp="210rqelrus41w">
        <Header title="Pay Bill" onBackClick={() => navigateTo('summary', -1)} data-id="element-802" data-mp="1s0bohgqrd4z5" />
        <div className="px-6 space-y-5" data-id="element-803" data-mp="1x0hih3ki33ke">
          <div className={`rounded-2xl p-4 flex items-center justify-between ${cardSurface}`} data-id="element-804" data-mp="1x1ix1i0q686v">
            <div data-id="element-805" data-mp="1i4rs6t8a849v">
              <p className={`text-[11px] uppercase tracking-wider ${textSec}`} data-id="element-806" data-mp="1i5beem4ytetp">
                Due Date
              </p>
              <p className={`text-[15px] font-bold ${textPri} mt-0.5`} data-id="element-807" data-mp="3ch7zlxctdogb">
                {CREDIT_CARD.dueDate}
              </p>
            </div>
            
          </div>
          <div data-id="element-808" data-mp="3hdox3i5wlf62">
            <p className={`text-[12px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-809" data-mp="3fym6iirnl3q0">
              Pay From
            </p>
            <button onClick={() => setShowAccountDropdown(!showAccountDropdown)} className={`w-full rounded-2xl p-4 flex items-center justify-between ${cardSurface}`} data-id="element-810" data-mp="3b1vrlyrl4e66">
              <div className="text-left" data-id="element-811" data-mp="320fsin67zyh9">
                <p className={`text-[14px] font-bold ${textPri}`} data-id="element-812" data-mp="321h5wyapkx25">
                  {payFrom.label}
                </p>
                <p className={`text-[12px] ${textSec}`} data-id="element-813" data-mp="3tqkbchec0dgd">
                  {payFrom.number} · Bal: PKR {formatPKR(payFrom.balance)}
                </p>
              </div>
              <ChevronDownIcon className={`w-5 h-5 ${textSec}`} data-id="element-814" data-mp="3oxhfu3pzutxj" />
            </button>
            {showAccountDropdown && <div className={`mt-2 rounded-2xl overflow-hidden ${cardSurface}`} data-id="element-815" data-mp="3kw5sk4uleksi">
                {PAY_FROM_ACCOUNTS.map(acc => <button key={acc.id} onClick={() => {
              setPayFrom(acc);
              setShowAccountDropdown(false);
            }} className={`w-full text-left p-4 flex items-center justify-between ${payFrom.id === acc.id ? isDark ? 'bg-[#3D2518]' : 'bg-[#FFF7F0]' : ''}`} data-id="element-816" data-mp="gk0nwkvxer2i">
                    <div data-id="element-817" data-mp="2876t7cfnqan9">
                      <p className={`text-[14px] font-bold ${textPri}`} data-id="element-818" data-mp="286xb0b0ut7s4">
                        {acc.label}
                      </p>
                      <p className={`text-[12px] ${textSec}`} data-id="element-819" data-mp="2d7upaciqthoc">
                        {acc.number} · PKR {formatPKR(acc.balance)}
                      </p>
                    </div>
                    {payFrom.id === acc.id && <CheckIcon className="w-5 h-5 text-[#F37024]" data-id="element-820" data-mp="2n2mmvgcbs4j4" />}
                  </button>)}
              </div>}
          </div>
          <div data-id="element-821" data-mp="2vwrl4ktsgi8o">
            <p className={`text-[12px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-[#F37024]' : 'text-[#5A1408]'}`} data-id="element-822" data-mp="2vxasj7ltbo8g">
              Enter Amount
            </p>
            <div className={`rounded-2xl p-4 flex items-baseline gap-2 ${cardSurface}`} data-id="element-823" data-mp="30u1c44wtt1ym">
              <span className={`text-[16px] font-bold ${textSec}`} data-id="element-824" data-mp="35smarzwfjdky">PKR</span>
              <input type="tel" value={payAmount === '' ? '' : Number(payAmount).toLocaleString('en-US')} onChange={e => setPayAmount(e.target.value.replace(/[^0-9]/g, ''))} placeholder="0" className={`flex-1 outline-none bg-transparent text-[28px] font-bold ${textPri}`} data-id="element-825" data-mp="2gq8mjthnm9ap" />
            </div>
          </div>
          <button disabled={!isValid} onClick={() => navigateTo('review')} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform disabled:opacity-40" data-id="element-826" data-mp="2lro3b9csttna">
            Continue
          </button>
        </div>
      </motion.div>;
  };
  const renderReview = () => {
    const numericAmount = Number(payAmount.replace(/[^0-9]/g, '')) || 0;
    return <motion.div key="review" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-827" data-mp="2212m6272yd18">
        <Header title="Review" onBackClick={() => navigateTo('pay_bill', -1)} data-id="element-828" data-mp="1082lxfaxck1g" />
        <div className="px-5 flex-1 flex flex-col pb-6 space-y-4" data-id="element-829" data-mp="105y927v1sap1">
          <div className={`rounded-2xl px-5 py-5 text-center ${cardSurface}`} data-id="element-830" data-mp="1502g0r4bciq4">
            <div className="flex items-baseline justify-center gap-2" data-id="element-831" data-mp="qao6nj0qm1aq">
              <span className="text-[16px] font-semibold text-[#F37024]" data-id="element-832" data-mp="v8g7x3n9uzrl">
                PKR
              </span>
              <span className={`text-[42px] font-bold leading-none ${textPri}`} data-id="element-833" data-mp="1iy0sts2zk73i">
                {numericAmount.toLocaleString('en-US')}
              </span>
            </div>
          </div>
          <div className={`rounded-2xl px-5 py-5 ${cardSurface}`} data-id="element-834" data-mp="190mbzrd5hgao">
            <div className="flex gap-4 pb-4 border-b border-[rgba(0,0,0,0.05)]" data-id="element-835" data-mp="192gyvb7zfihe">
              <div className="flex-1 min-w-0" data-id="element-836" data-mp="1dxct8i0oyhxo">
                <p className="text-[14px] font-bold mb-1 text-[#C84B11]" data-id="element-837" data-mp="3tptyvjy85a19">
                  Pay From
                </p>
                <p className={`text-[13px] ${textPri}`} data-id="element-838" data-mp="2l3jsp1gqjw6">{payFrom.holder}</p>
                <p className={`text-[12px] ${textSec}`} data-id="element-839" data-mp="19pefqghb5t5m">{payFrom.label}</p>
              </div>
            </div>
            <div className="flex gap-4 pt-4" data-id="element-840" data-mp="1ep94addm26ni">
              <div className="flex-1 min-w-0" data-id="element-841" data-mp="1emwddapvklyb">
                <p className="text-[14px] font-bold mb-1 text-[#C84B11]" data-id="element-842" data-mp="10pzp9h155il4">
                  Pay To
                </p>
                <p className={`text-[13px] ${textPri}`} data-id="element-843" data-mp="15p3y26k5leax">Credit Card</p>
                <p className={`text-[12px] ${textSec}`} data-id="element-844" data-mp="1tddfkwac3fw9">{CREDIT_CARD.number}</p>
              </div>
            </div>
          </div>
          <div className="flex-1" data-id="element-845" data-mp="1xe749rvloyq7" />
          <button onClick={() => navigateTo('processing')} className="w-full py-4 bg-[#5A1408] text-white font-bold text-[16px] rounded-2xl shadow-md active:scale-[0.98] transition-transform" data-id="element-846" data-mp="1xhbf383kmspu">
            Confirm Payment
          </button>
        </div>
      </motion.div>;
  };
  const renderProcessing = () => <motion.div key="processing" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen flex flex-col items-center justify-center ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-847" data-mp="2cawpe24m92jq">
      <motion.div animate={{
      rotate: 360
    }} transition={{
      duration: 1,
      repeat: Infinity,
      ease: 'linear'
    }} className="mb-6" data-id="element-848" data-mp="2mfmxkpqm52we">
        <RefreshCwIcon className="w-14 h-14 text-[#F37024]" strokeWidth={1.5} data-id="element-849" data-mp="2v8zyadux58t1" />
      </motion.div>
      <p className={`text-[20px] font-bold mb-2 ${textPri}`} data-id="element-850" data-mp="27jfdrqj4a3hn">Processing...</p>
    </motion.div>;
  return <div className={`w-full min-h-screen relative overflow-hidden ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-851" data-mp="23htthk496mxf">
      <AnimatePresence mode="wait" custom={direction} data-id="element-852" data-mp="2hhbpajfyv0ga">
        {step === 'tabs' && renderTabs()}
        {step === 'summary' && renderSummary()}
        {step === 'manage_cards' && renderManageCards()}
        {step === 'card_detail' && renderCardDetail()}
        {step === 'card_controls' && renderCardControls()}
        {step === 'spending_limits' && renderSpendingLimits()}
        {step === 'blocked_merchants' && renderBlockedMerchants()}
        {step === 'pin_management' && renderPinManagement()}
        {step === 'pin_enter_new' && renderPinEnterNew()}
        {step === 'pin_confirm_new' && renderPinConfirmNew()}
        {step === 'pin_success' && <motion.div key="pin_success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-853" data-mp="8ugiey3cm8y7">
            <SuccessView title="PIN Changed" subtitle="Your card PIN has been successfully updated." actionText="Back to Card Details" onAction={() => navigateTo('card_detail', -1)} data-id="element-854" data-mp="3h9gp233fcesw" />
          </motion.div>}
        {step === 'activation_entry' && renderActivationEntry()}
        {step === 'activation_details' && renderActivationDetails()}
        {step === 'activation_set_pin' && renderActivationSetPin()}
        {step === 'activation_success' && <motion.div key="activation_success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-855" data-mp="2yhxo01synati">
            <SuccessView title="Card Activated" subtitle="Your card is now ready to use." actionText="Back to Card Details" onAction={() => navigateTo('card_detail', -1)} data-id="element-856" data-mp="2m153hhj1zfgq" />
          </motion.div>}
        {step === 'request_entry' && renderRequestEntry()}
        {step === 'request_type' && renderRequestType()}
        {step === 'request_delivery' && renderRequestDelivery()}
        {step === 'request_review' && renderRequestReview()}
        {step === 'request_processing' && renderProcessing()}
        {step === 'request_success' && <motion.div key="request_success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-857" data-mp="1tbpn0c8mcudf">
            <SuccessView title="Request Submitted" subtitle="Your new card will arrive in 3-5 business days." actionText="Back to Cards" onAction={() => navigateTo('tabs', -1)} data-id="element-858" data-mp="1kc2lzrjsxqkw" />
          </motion.div>}
        {step === 'block_entry' && renderBlockEntry()}
        {step === 'block_reason' && renderBlockReason()}
        {step === 'block_confirm' && renderBlockConfirm()}
        {step === 'block_success' && <motion.div key="block_success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-859" data-mp="249vy4p8m033a">
            <SuccessView title="Card Blocked" subtitle="Your card has been successfully blocked." actionText="Back to Cards" onAction={() => navigateTo('tabs', -1)} data-id="element-860" data-mp="1vbkc70iotth6" />
          </motion.div>}
        {step === 'toggle_confirm' && renderToggleConfirm()}
        {step === 'toggle_success' && <motion.div key="toggle_success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-861" data-mp="3bptxtmvjp44b">
            <SuccessView title="Settings Updated" subtitle="Your card controls have been updated." actionText="Back to Controls" onAction={() => navigateTo('card_controls', -1)} data-id="element-862" data-mp="140o8nq3vzhlp" />
          </motion.div>}
        {step === 'pay_bill' && renderPayBill()}
        {step === 'review' && renderReview()}
        {step === 'processing' && renderProcessing()}
        {step === 'success' && <motion.div key="success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-863" data-mp="1np6ovi6wzko9">
            <SuccessView title="Payment Successful" subtitle="Your credit card bill has been paid." actionText="Back to Summary" onAction={() => navigateTo('summary', -1)} data-id="element-864" data-mp="1wpbv3p0dgd0u" />
          </motion.div>}
        {step === 'apply_account' && renderApplyAccount()}
        {step === 'apply_scheme' && renderApplyScheme()}
        {step === 'apply_choose_card' && renderApplyChooseCard()}
        {step === 'apply_name_delivery' && renderApplyNameDelivery()}
        {step === 'apply_review' && renderApplyReview()}
        {step === 'apply_processing' && renderProcessing()}
        {step === 'apply_success' && <motion.div key="apply_success" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className={`w-full min-h-screen pb-8 flex flex-col ${isDark ? 'bg-[#1A0E08]' : 'bg-page-warm'}`} data-id="element-865" data-mp="27dg56cllktse">
            <SuccessView title="Application Submitted" subtitle="Your new card will arrive in 3-5 business days." actionText="Back to Cards" onAction={() => navigateTo('tabs', -1)} data-id="element-866" data-mp="2le8zoxbwfovi" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}